import { printTicketPdf } from "../../lib/ticketPrint";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, UPLOADS_BASE, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { ShareDialog } from "../../components/ShareDialog";
import { CategoryIcon } from "../../lib/categoryIcons";
import { Plus, Minus, Printer, FileDown, Search, Eye, Share2, Undo2, Ban, ShieldCheck, ShieldAlert, Lock, FileArchive, FileBadge, Contact, X, Pencil, Percent, CreditCard, Banknote, FileCheck2, Wallet, Trash2, ArrowLeft, Mail, PackageX, Star, Clock, CalendarDays, Scissors, Package, ArrowDownToLine, ArrowUpFromLine, LogOut, ScrollText, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { PriseRdvTab, RdvFormDialog } from "./PriseRdvTab";
import { rdvVisualStatus, useNow } from "../../lib/calendarUtils";
import { NumericKeypad } from "../../components/NumericKeypad";

const UNITE_LABELS = { kg: "kg", m2: "m²", m3: "m³" };
const uniteDe = (a) => UNITE_LABELS[a?.unite_vente] || "kg";

// Formatte la PÉRIODE COUVERTE par une clôture (distincte de la date à
// laquelle elle a été effectuée, affichée séparément) — ex. une clôture
// journalière du 10/09/2026 réalisée automatiquement le 11/09/2026 à 00h15
// doit clairement indiquer "10/09/2026", pas seulement la date d'exécution.
function formatPeriodeCouverte(c) {
  if (!c.periode_debut) return null;
  const debut = new Date(c.periode_debut);
  const dateDebut = debut.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
  if (c.periode === "jour") return dateDebut;
  if (c.periode === "mois") return debut.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", month: "long", year: "numeric" });
  if (c.periode === "annee") return debut.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", year: "numeric" });
  if (!c.periode_fin) return dateDebut;
  const dateFin = new Date(c.periode_fin).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" });
  return dateDebut === dateFin ? dateDebut : `du ${dateDebut} au ${dateFin}`;
}

// Pastilles indiquant la progression de saisie d'un code PIN à 3 chiffres —
// le pavé numérique lui-même est le composant partagé <NumericKeypad>, le
// même que celui de l'écran de connexion, pour une apparence et un
// comportement identiques (y compris le clavier physique, géré par
// l'appelant via un useEffect dédié — voir plus bas).
function PinDots({ value, maxLength = 3, testidPrefix = "pin-keypad" }) {
  return (
    <div className="flex items-center justify-center gap-3" data-testid={`${testidPrefix}-dots`}>
      {Array.from({ length: maxLength }).map((_, i) => (
        <div key={i} className={`h-3.5 w-3.5 rounded-full border border-border ${i < value.length ? "bg-foreground" : "bg-transparent"}`} />
      ))}
    </div>
  );
}

const PIN_KEYPAD_KEY_CLASS =
  "min-h-[56px] rounded-lg border border-border text-lg font-semibold hover:border-primary active:scale-95 transition-transform duration-100 disabled:opacity-50";

export default function CaissePage() {
  const { user } = useAuth();
  const location = useLocation();
  // Devis transformé en facture (bouton "Transformer en facture" de
  // DevisFormPage, qui navigue ici avec ces infos dans le state du routeur) :
  // on pré-remplit le panier/client une seule fois au montage, puis on
  // rattache la facture créée au devis d'origine via source_devis_id.
  const [sourceDevisId, setSourceDevisId] = useState(null);
  // Reprise (rachat d'appareil) clôturée passée en caisse (bouton « Créer une vente »
  // de RepriseFormPage) : ligne négative TVA 0 %, montant relu en base par le serveur.
  const [sourceRepriseId, setSourceRepriseId] = useState(null);
  // Acompte sur devis (bouton « Encaisser un acompte » de DevisFormPage) : ticket
  // d'une seule ligne « Acompte devis n°… », rattaché au devis par le serveur.
  const [acompteDevisId, setAcompteDevisId] = useState(null);
  const [acompteDialog, setAcompteDialog] = useState(null); // { numero, reste, suggere, tva_percent }
  const [acompteMontant, setAcompteMontant] = useState("");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  // Par défaut, la liste de vente ne montre que les articles réellement en
  // stock (quantité > 0) : c'est l'usage courant à la caisse, et ça évite de
  // noyer l'opérateur dans des articles qu'il ne peut de toute façon pas
  // vendre. Un article en rupture reste accessible via ce bouton pour les cas
  // particuliers (vente sur commande, régularisation...).
  const [showOutOfStock, setShowOutOfStock] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [cart, setCart] = useState([]);
  const [articleSearch, setArticleSearch] = useState("");
  // Sélecteur "Prestation" / "Produit" de l'onglet Vente en modèle "Sur
  // rendez-vous" (remplace la recherche + liste par un choix à 2 catégories,
  // dans le même esprit visuel que la grille tactile — voir plus bas).
  const [venteRdvCategorie, setVenteRdvCategorie] = useState("prestation");
  const [venteRdvArticleSearch, setVenteRdvArticleSearch] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  // Tickets antérieurs (voir bouton dédié) : liste complète, pas seulement
  // celle du jour affichée en ligne — avec sa propre recherche et un filtre
  // par date précise.
  const [anteriorDialogOpen, setAnteriorDialogOpen] = useState(false);
  const [anteriorSearch, setAnteriorSearch] = useState("");
  const [anteriorDate, setAnteriorDate] = useState("");
  const [prestationName, setPrestationName] = useState("");
  // Prix de la prestation libre saisi en TTC, avec son propre taux de TVA (la TVA
  // ne se modifie plus globalement à la caisse : elle vient de l'article en stock,
  // ou est définie ici ligne par ligne pour une prestation libre).
  const [prestationPrice, setPrestationPrice] = useState("");
  const [prestationTva, setPrestationTva] = useState(20);
  const [docType, setDocType] = useState("ticket");
  const [clientInfo, setClientInfo] = useState({ nom: "", adresse: "", email: "", siret: "", siren: "", tva_intracom: "", adresse_livraison: "" });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);

  // Prefill venant d'un devis accepté (bouton "Transformer en facture" de
  // DevisFormPage). On ne lit location.state qu'une fois au montage : une
  // fois consommé, un re-render ne doit pas re-déclencher le prefill (sinon
  // vider le panier manuellement le ré-remplirait tout seul).
  useEffect(() => {
    const fromAcompte = location.state?.fromDevisAcompte;
    if (fromAcompte) {
      setDocType("ticket");
      setAcompteDevisId(fromAcompte.id);
      setAcompteDialog(fromAcompte);
      setAcompteMontant(Number(fromAcompte.suggere || 0).toFixed(2));
      if (fromAcompte.client_id) setSelectedClient({ id: fromAcompte.client_id, nom: fromAcompte.client_nom });
      return;
    }
    const fromReprise = location.state?.fromReprise;
    if (fromReprise) {
      setDocType("ticket");
      setSourceRepriseId(fromReprise.id);
      setCart([{
        type: "reprise_deduction", article_id: null,
        nom: `Reprise n°${fromReprise.numero}${fromReprise.client_nom ? ` – ${fromReprise.client_nom}` : ""}`,
        prix_unitaire: -Math.abs(Number(fromReprise.offre_rachat) || 0), prix_origine: null, quantite: 1,
        remise_label: null, tva_percent: 0, prix_achat_ht: 0, eco_participation_ht: 0, verrouille: true, sans_suppression: true,
      }]);
      setClientInfo((c) => ({ ...c, nom: fromReprise.client_nom || "", adresse: fromReprise.client_adresse || "", email: fromReprise.client_email || "" }));
      if (fromReprise.client_id) setSelectedClient({ id: fromReprise.client_id, nom: fromReprise.client_nom });
      toast.message(`Reprise ${fromReprise.numero} chargée en négatif : ajoutez les articles de la vente.`);
      return;
    }
    const fromDevis = location.state?.fromDevis;
    if (!fromDevis) return;
    setDocType("facture");
    // Acomptes déjà encaissés : lignes négatives « Acompte devis n°… » (le serveur
    // recalcule exactement les mêmes montants à la validation).
    if (fromDevis.id) {
      api.get(`/devis/${fromDevis.id}/acomptes`).then(({ data }) => {
        if (!data.a_deduire?.length) return;
        setCart((c) => [
          ...c.filter((i) => i.type !== "acompte_deduction"),
          ...data.a_deduire.map((l) => ({
            type: "acompte_deduction", article_id: null, nom: l.nom, prix_unitaire: l.prix_unitaire, prix_origine: null, quantite: 1,
            remise_label: null, tva_percent: l.tva_percent, prix_achat_ht: 0, eco_participation_ht: 0, verrouille: true, sans_suppression: true,
            acompte_ticket_numero: l.acompte_ticket_numero,
          })),
        ]);
        toast.message(`Acompte(s) déduit(s) : ${Number(data.a_deduire.reduce((s, l) => s + l.montant_ttc, 0)).toFixed(2)} €`);
      }).catch(() => {});
    }
    setSourceDevisId(fromDevis.id || null);
    if (fromDevis.items?.length) {
      setCart(
        fromDevis.items.map((it) => ({
          type: "prestation",
          article_id: it.article_id || null,
          nom: it.nom,
          prix_unitaire: Number(it.prix_unitaire) || 0,
          prix_origine: null,
          quantite: Number(it.quantite) || 1,
          remise_label: null,
          tva_percent: it.tva_percent ?? 20,
          prix_achat_ht: 0,
          eco_participation_ht: 0,
        }))
      );
    }
    setClientInfo((c) => ({
      ...c,
      nom: fromDevis.client_nom || "",
      adresse: fromDevis.client_adresse || "",
      email: fromDevis.client_email || "",
    }));
    if (fromDevis.client_id) {
      setSelectedClient({ id: fromDevis.client_id, nom: fromDevis.client_nom });
    }
    toast.message(
      `Devis ${fromDevis.numero || ""} chargé — vérifiez le panier avant d'encaisser.`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Programme de fidélité activable (voir fidelite_utils.py) ----
  // Réglages (activation, taux, valeur du point, paliers cartes cadeaux)
  // gérés depuis Administration > Fidélité — ici on ne fait que LIRE l'état
  // courant, pour savoir si la fonction doit être proposée à la caisse.
  const [fidelite, setFidelite] = useState(null); // { active, taux_points_par_euro, paliers_cartes_cadeaux }

  const loadFidelite = async () => {
    try {
      const { data } = await api.get("/caisse/fidelite-settings");
      setFidelite(data);
    } catch {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };

  useEffect(() => { loadFidelite(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Utilisation de points fidélité sur la vente en cours ----
  // Un seul jeu de paliers (configuré dans Administration > Fidélité) pour
  // les deux usages possibles : réduction directe ici, ou échange contre une
  // carte cadeau (onglet Carte cadeau) — plus de taux de conversion libre,
  // qui faisait doublon avec les paliers de carte cadeau.
  const [clientFideliteBalance, setClientFideliteBalance] = useState(0);
  const [selectedPalierPoints, setSelectedPalierPoints] = useState(""); // "" = aucun palier choisi

  useEffect(() => {
    if (!selectedClient || !fidelite?.active) { setClientFideliteBalance(0); setSelectedPalierPoints(""); return; }
    api.get(`/caisse/fidelite/client/${selectedClient.id}`)
      .then(({ data }) => setClientFideliteBalance(data.fidelite_points || 0))
      .catch(() => setClientFideliteBalance(0));
  }, [selectedClient, fidelite]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Cartes cadeaux (voir carte_cadeau_utils.py) ----
  const [cartesCadeaux, setCartesCadeaux] = useState([]);
  const [genererDialogOpen, setGenererDialogOpen] = useState(false);
  const [genererMontant, setGenererMontant] = useState("");
  const [genererValiditeJours, setGenererValiditeJours] = useState("");
  const [genererSubmitting, setGenererSubmitting] = useState(false);
  const [viaFideliteDialogOpen, setViaFideliteDialogOpen] = useState(false);
  const [viaFideliteClient, setViaFideliteClient] = useState(null);
  const [viaFideliteClientSearch, setViaFideliteClientSearch] = useState("");
  const [viaFideliteClientResults, setViaFideliteClientResults] = useState([]);
  const [viaFideliteBalance, setViaFideliteBalance] = useState(null);
  const [viaFideliteSubmitting, setViaFideliteSubmitting] = useState(false);
  const [lastGeneratedCarte, setLastGeneratedCarte] = useState(null);
  const [carteCadeauSearch, setCarteCadeauSearch] = useState("");
  // Annulation d'une carte cadeau — motif obligatoire (traçabilité).
  const [annulerCarte, setAnnulerCarte] = useState(null);
  const [annulerCarteMotif, setAnnulerCarteMotif] = useState("");
  const [annulerCarteSubmitting, setAnnulerCarteSubmitting] = useState(false);

  const submitAnnulerCarte = async () => {
    if (!annulerCarteMotif.trim()) { toast.error("Le motif d'annulation est obligatoire"); return; }
    setAnnulerCarteSubmitting(true);
    try {
      await api.post(`/caisse/cartes-cadeaux/${annulerCarte.id}/annuler`, { motif: annulerCarteMotif.trim() });
      toast.success("Carte cadeau annulée");
      setAnnulerCarte(null);
      loadCartesCadeaux();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAnnulerCarteSubmitting(false);
    }
  };

  const loadCartesCadeaux = async () => {
    try {
      const { data } = await api.get("/caisse/cartes-cadeaux");
      setCartesCadeaux(data);
    } catch {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };
  useEffect(() => { loadCartesCadeaux(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitGenererCarte = async () => {
    const montant = Number(genererMontant);
    if (!montant || montant <= 0) { toast.error("Indiquez un montant valide"); return; }
    setGenererSubmitting(true);
    try {
      const { data } = await api.post("/caisse/cartes-cadeaux", {
        montant, validite_jours: genererValiditeJours ? Number(genererValiditeJours) : null,
      });
      toast.success(`Carte cadeau ${data.code} générée`);
      setGenererDialogOpen(false);
      setGenererMontant("");
      setGenererValiditeJours("");
      setLastGeneratedCarte(data);
      loadCartesCadeaux();
      window.open(`${api.defaults.baseURL}/caisse/cartes-cadeaux/${data.id}/pdf`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenererSubmitting(false);
    }
  };

  useEffect(() => {
    if (!viaFideliteClientSearch.trim()) { setViaFideliteClientResults([]); return; }
    const timeout = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { q: viaFideliteClientSearch.trim() } });
        setViaFideliteClientResults(data.slice(0, 8));
      } catch {
        setViaFideliteClientResults([]);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [viaFideliteClientSearch]);

  const selectViaFideliteClient = async (client) => {
    setViaFideliteClient(client);
    setViaFideliteClientSearch("");
    setViaFideliteClientResults([]);
    try {
      const { data } = await api.get(`/caisse/fidelite/client/${client.id}`);
      setViaFideliteBalance(data.fidelite_points || 0);
    } catch {
      setViaFideliteBalance(0);
    }
  };

  const submitCarteViaFidelite = async (points) => {
    if (!viaFideliteClient) return;
    setViaFideliteSubmitting(true);
    try {
      const { data } = await api.post("/caisse/cartes-cadeaux/via-fidelite", { client_id: viaFideliteClient.id, points });
      toast.success(`Carte cadeau ${data.code} générée (${points} points utilisés)`);
      setViaFideliteDialogOpen(false);
      setViaFideliteClient(null);
      setViaFideliteBalance(null);
      loadCartesCadeaux();
      window.open(`${api.defaults.baseURL}/caisse/cartes-cadeaux/${data.id}/pdf`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setViaFideliteSubmitting(false);
    }
  };

  const [shareItem, setShareItem] = useState(null);
  const [annulerTicket, setAnnulerTicket] = useState(null);
  const [annulerMotif, setAnnulerMotif] = useState("");
  const [facturerTicket, setFacturerTicket] = useState(null);
  const [facturerInfo, setFacturerInfo] = useState({ nom: "", adresse: "", email: "", siret: "", siren: "", tva_intracom: "", adresse_livraison: "" });
  // Dialogue d'avoir : choix du moyen de remboursement (indispensable pour que
  // le fond de caisse reflète correctement une sortie d'espèces du tiroir).
  const [avoirTicket, setAvoirTicket] = useState(null);
  const [avoirMode, setAvoirMode] = useState("");
  const [avoirEmettreCarteCadeau, setAvoirEmettreCarteCadeau] = useState(false);
  const [avoirCarteCadeauValidite, setAvoirCarteCadeauValidite] = useState("");
  const [avoirChequeNumero, setAvoirChequeNumero] = useState("");
  const [avoirChequeNom, setAvoirChequeNom] = useState("");
  const [avoirMotif, setAvoirMotif] = useState("");
  const [avoirSaving, setAvoirSaving] = useState(false);
  // Avoir partiel : quantités à rembourser par ligne du document d'origine ({index: quantité}).
  const [avoirPartiel, setAvoirPartiel] = useState(false);
  const [avoirLignes, setAvoirLignes] = useState({});
  const [mailTicket, setMailTicket] = useState(null);
  const [mailTo, setMailTo] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailMessage, setMailMessage] = useState("");
  const [mailSending, setMailSending] = useState(false);

  // Modification manuelle du prix / réduction sur une ligne du panier (avant validation
  // du ticket — le document créé reste, lui, inaltérable une fois émis).
  const [editPriceIdx, setEditPriceIdx] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [discountIdx, setDiscountIdx] = useState(null);
  const [discountType, setDiscountType] = useState("percent"); // "percent" | "amount"
  const [discountValue, setDiscountValue] = useState("");

  const [clotures, setClotures] = useState([]);
  const [chainStatus, setChainStatus] = useState(null);
  const [cumuls, setCumuls] = useState(null);

  // Destinataires de l'envoi automatique des clôtures (archive ZIP complète :
  // ticket Z + PDF de chaque document + manifeste JSON), déclenché chaque
  // nuit à 00h15 (heure de Paris, une fois la journée entièrement terminée)
  // — voir `compta_emails` de la boutique. Gérés via un petit menu (ajouter/
  // modifier/supprimer) plutôt qu'un champ unique.
  const [comptaEmails, setComptaEmails] = useState([]);
  const [comptaEmailMenuOpen, setComptaEmailMenuOpen] = useState(false);
  const [comptaEmailSaving, setComptaEmailSaving] = useState(false);
  const [newComptaEmail, setNewComptaEmail] = useState("");
  const [editingComptaEmailIndex, setEditingComptaEmailIndex] = useState(null);
  const [editingComptaEmailValue, setEditingComptaEmailValue] = useState("");
  // Second destinataire indépendant (avocat, tiers de confiance...) pour
  // l'empreinte fiscale externe mensuelle — voir scheduler_utils.py.
  const [anchorEmailSecondaire, setAnchorEmailSecondaire] = useState("");
  // Affichage du prix de chaque article sur le ticket de caisse : "ttc" ou
  // "ht" (voir generate_ticket_pdf). N'affecte jamais les totaux HT/TVA/TTC
  // généraux du document, seulement le montant imprimé en face de chaque
  // article.
  const [ticketPrixAffichage, setTicketPrixAffichage] = useState("ttc");
  const [ticketPrixAffichageSaving, setTicketPrixAffichageSaving] = useState(false);

  // --- Encaissement multi-moyens de paiement ---------------------------------
  // Moyens de paiement actifs (configurés dans Administration > Moyens de paiement).
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  // Paiements déjà appliqués à la vente en cours (avant validation définitive).
  const [payments, setPayments] = useState([]);
  const [montantInput, setMontantInput] = useState("");
  // Sous-dialogue chèque : moyen "chèque" en attente de n° + nom avant d'être ajouté.
  const [chequePending, setChequePending] = useState(null); // { montant }
  const [chequeNumero, setChequeNumero] = useState("");
  const [chequeNom, setChequeNom] = useState("");
  // Sous-dialogue carte cadeau : moyen "carte_cadeau" en attente d'un code à
  // 12 chiffres, vérifié à la volée avant d'être ajouté (voir
  // carte_cadeau_utils.py). Autorisation réelle refaite côté serveur à la
  // création du ticket, jamais uniquement sur la base de cette vérification.
  const [carteCadeauPending, setCarteCadeauPending] = useState(null); // { montant, method }
  const [carteCadeauCodeInput, setCarteCadeauCodeInput] = useState("");
  const [carteCadeauVerifying, setCarteCadeauVerifying] = useState(false);
  const [carteCadeauVerifyResult, setCarteCadeauVerifyResult] = useState(null); // { valid, solde, message }
  // Plafond légal de paiement en espèces dépassé (1 000 €, art. L112-6 du code
  // monétaire et financier) : révèle l'option "client non-résident fiscal"
  // (plafond porté à 15 000 €, voir validate()) au lieu de bloquer sans
  // alternative — jamais affichée tant que le serveur n'a pas refusé une
  // première tentative.
  const [cashCeilingBlocked, setCashCeilingBlocked] = useState(false);
  const [nonResidentIdRef, setNonResidentIdRef] = useState("");
  const [nonResidentIdType, setNonResidentIdType] = useState("");
  const [validating, setValidating] = useState(false);
  const idemKeyRef = useRef(null);
  // Réglage boutique (impression automatique du ticket, exemption AGEC) — voir
  // la note légale sur `validate()` ci-dessous. `undefined` = pas encore
  // chargé ; on considère alors, par prudence, que l'impression n'est PAS
  // automatique (comportement par défaut légal) tant que le réglage réel
  // n'est pas connu.
  const [shopSettings, setShopSettings] = useState(undefined);
  // Dernier document créé quand l'impression automatique est désactivée (voir
  // `validate()`) : affiche un bouton "Imprimer le ticket" ponctuel, sur
  // demande du client, plutôt que d'ouvrir le PDF sans qu'il le demande.
  const [lastCreatedDoc, setLastCreatedDoc] = useState(null);
  // Facturation électronique (plateforme agréée) : statuts des factures, dialogue « PA injoignable ».
  const [paStatuses, setPaStatuses] = useState({});
  const [paBlock, setPaBlock] = useState(null); // { finalPayments, nonResidentOverride }

  // Gestion par table (modèle "par_table") : chaque table est une session de
  // panier persistée côté serveur (voir table_sessions_routes.py) — le
  // panier/paiement restent ceux, entièrement partagés, de tous les modèles.
  const [tables, setTables] = useState([]);
  const [activeTableId, setActiveTableId] = useState(null);
  const [newTableNumero, setNewTableNumero] = useState("");
  const activeTable = tables.find((t) => t.id === activeTableId) || null;

  const loadTables = async () => {
    try {
      const { data } = await api.get("/table-sessions");
      setTables(data);
    } catch (e) { /* silencieux : affiché seulement si le modèle est actif */ }
  };

  const ouvrirTable = async () => {
    if (!newTableNumero.trim()) { toast.error("Indiquez un numéro ou nom de table"); return; }
    try {
      const { data } = await api.post("/table-sessions", { numero_table: newTableNumero.trim() });
      setNewTableNumero("");
      await loadTables();
      setCart(data.lignes || []);
      setActiveTableId(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const reprendreTable = async (table) => {
    setCart(table.lignes || []);
    setActiveTableId(table.id);
  };

  const changerDeTable = () => {
    setActiveTableId(null);
    setCart([]);
    loadTables();
  };

  const fermerTableSansEncaisser = async (tableId) => {
    if (!window.confirm("Fermer cette table sans encaisser ? Le panier en cours sera perdu.")) return;
    try {
      await api.delete(`/table-sessions/${tableId}`);
      if (activeTableId === tableId) { setActiveTableId(null); setCart([]); }
      loadTables();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };


  // --- Fond de caisse / session de caisse (contrôle interne, non bloquant) ---
  const [currentSession, setCurrentSession] = useState(undefined); // undefined = pas encore chargé, null = aucune session ouverte
  const [sessionOpenDialog, setSessionOpenDialog] = useState(false);
  const [sessionCloseDialog, setSessionCloseDialog] = useState(false);
  const [fondOuverture, setFondOuverture] = useState("");
  const [fondFermetureReel, setFondFermetureReel] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [sessionSaving, setSessionSaving] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  // Étape 2 de la fermeture de caisse : aperçu de l'écart calculé (avant
  // confirmation définitive), renvoyé par /session/{id}/preview-close.
  const [closePreview, setClosePreview] = useState(null);

  // --- Mode multi-caisses (voir shops_routes.CAISSE_MODES) ---
  // Menu "Choisissez la caisse à ouvrir" affiché au clic sur "Ouvrir la
  // caisse" quand la boutique a plusieurs caisses (shopSettings.caisse_mode
  // === "multiple") : liste chaque caisse (1..nombre_caisses) avec son état
  // (libre -> "Ouvrir", déjà ouverte par quelqu'un -> "Rejoindre").
  const [caisseChoiceDialog, setCaisseChoiceDialog] = useState(false);
  const [openSessions, setOpenSessions] = useState([]); // toutes les caisses actuellement ouvertes pour la boutique
  const [selectedCaisseNumero, setSelectedCaisseNumero] = useState(1); // caisse choisie dans le menu, avant saisie du fond de départ
  // Entrée/sortie manuelle d'espèces (appoint, dépôt en banque...) sur la
  // caisse actuellement ouverte/rejointe par ce vendeur.
  const [sessionMouvementDialog, setSessionMouvementDialog] = useState(false);
  const [mouvementType, setMouvementType] = useState("entree");
  const [mouvementMontant, setMouvementMontant] = useState("");
  const [mouvementMotif, setMouvementMotif] = useState("");
  const [mouvementSaving, setMouvementSaving] = useState(false);
  // Journal détaillé d'UNE session (qui a ouvert/fermé/rejoint/quitté, entrées/
  // sorties d'espèces) — affiché via le bouton "Journal" de chaque ligne de
  // "Fond de caisse — historique des sessions" (onglet Registre fiscal).
  const [journalDialogSession, setJournalDialogSession] = useState(null);

  // Caisse (numéro) que CE navigateur a ouverte/rejointe pour cette boutique —
  // mémorisé localement pour retrouver la bonne session au rechargement de la
  // page (GET /session/current a besoin de ce numéro en mode multi-caisses,
  // sans quoi il ne saurait pas laquelle des caisses ouvertes en parallèle
  // concerne ce vendeur).
  const caisseNumeroStorageKey = () => `sogogestion_caisse_numero_${user?.effective_shop_id || "default"}`;
  const getStoredCaisseNumero = () => {
    try {
      const v = Number(localStorage.getItem(caisseNumeroStorageKey()));
      return v > 0 ? v : null;
    } catch { return null; }
  };
  const setStoredCaisseNumero = (n) => { try { localStorage.setItem(caisseNumeroStorageKey(), String(n)); } catch { /* stockage indisponible, tant pis */ } };
  const clearStoredCaisseNumero = () => { try { localStorage.removeItem(caisseNumeroStorageKey()); } catch { /* stockage indisponible, tant pis */ } };

  // --- Code PIN à 3 chiffres protégeant une caisse (mode multi-caisses) ---
  // À l'ouverture : bouton "PIN" sous le fond de départ, qui bascule le
  // dialogue sur un clavier numérique pour en définir un (optionnel).
  const [openPin, setOpenPin] = useState("");
  const [showOpenPinKeypad, setShowOpenPinKeypad] = useState(false);
  // Pour rejoindre : si la caisse choisie dans le menu a un PIN défini
  // (pin_set), un second dialogue demande de le ressaisir au clavier avant
  // d'appeler /join.
  const [joinPinDialog, setJoinPinDialog] = useState(null); // la session (caisse) à rejoindre, en attente du PIN
  const [joinPin, setJoinPin] = useState("");
  const [joinPinSaving, setJoinPinSaving] = useState(false);
  const [joinPinError, setJoinPinError] = useState("");

  const openPinDigit = (d) => {
    setOpenPin((p) => {
      const next = (p + d).slice(0, 3);
      if (next.length === 3) setTimeout(() => setShowOpenPinKeypad(false), 150);
      return next;
    });
  };
  const openPinBackspace = () => setOpenPin((p) => p.slice(0, -1));

  const joinPinDigit = (d) => {
    setJoinPinError("");
    setJoinPin((p) => (p + d).slice(0, 3));
  };
  const joinPinBackspace = () => { setJoinPinError(""); setJoinPin((p) => p.slice(0, -1)); };

  const openJoinPinDialog = (session) => {
    setJoinPinDialog(session);
    setJoinPin("");
    setJoinPinError("");
    setCaisseChoiceDialog(false);
  };

  const confirmJoinWithPin = async () => {
    if (joinPin.length !== 3) { toast.error("Saisissez les 3 chiffres du code PIN"); return; }
    setJoinPinSaving(true);
    try {
      const { data } = await api.post(`/caisse/session/${joinPinDialog.id}/join`, { pin: joinPin });
      setCurrentSession(data);
      setStoredCaisseNumero(data.caisse_numero);
      setJoinPinDialog(null);
      toast.success(`Caisse ${data.caisse_numero} rejointe`);
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      setJoinPinError(formatApiError(e.response?.data?.detail) || "Code PIN incorrect");
      setJoinPin("");
    } finally {
      setJoinPinSaving(false);
    }
  };

  // Clavier physique utilisable pour les deux pavés PIN, comme sur l'écran
  // de connexion : chiffres 0-9, Retour arrière, Entrée pour valider (une
  // fois les 3 chiffres saisis). Chaque useEffect n'est actif que pendant
  // que son dialogue précis est ouvert, pour ne jamais intercepter la
  // frappe ailleurs dans la page (recherche d'article, etc.).
  useEffect(() => {
    if (!showOpenPinKeypad) return;
    const onKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) { openPinDigit(e.key); return; }
      if (e.key === "Backspace") { openPinBackspace(); return; }
      if (e.key === "Enter" && openPin.length === 3) { setShowOpenPinKeypad(false); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOpenPinKeypad, openPin]);

  useEffect(() => {
    if (!joinPinDialog) return;
    const onKeyDown = (e) => {
      if (joinPinSaving) return;
      if (/^[0-9]$/.test(e.key)) { joinPinDigit(e.key); return; }
      if (e.key === "Backspace") { joinPinBackspace(); return; }
      if (e.key === "Enter") { confirmJoinWithPin(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinPinDialog, joinPinSaving, joinPin]);

  // Bug corrigé (demande explicite) : la permission "Supprimer un ticket /
  // une facture" (delete_ticket) donnait AUSSI accès à l'onglet Registre
  // fiscal, même si "Voir l'onglet fiscal" était décochée — les deux droits
  // sont pourtant des préoccupations distinctes (pouvoir annuler un ticket
  // depuis l'onglet Vente n'implique pas de devoir voir les clôtures, le
  // contrôle d'intégrité, l'archive et les réglages fiscaux). L'onglet ne
  // dépend donc plus que de "registre_fiscal" — les boutons d'annulation/
  // avoir restent, eux, inchangés et gérés séparément par "delete_ticket"
  // (voir canManageFiscal ci-dessous), directement sur chaque ligne de
  // ticket dans l'onglet Vente, qui n'a jamais dépendu de cet onglet.
  const canFiscal = user?.is_admin || hasPerm(user, "caisse", "registre_fiscal");
  // Droits sur les opérations fiscales sensibles (annulation, avoir) : reste
  // strictement lié à "delete_ticket", distinct de la simple consultation.
  const canManageFiscal = user?.is_admin || hasPerm(user, "caisse", "delete_ticket");
  // Premier onglet visible pour cet utilisateur — évite d'ouvrir la page sur
  // un onglet "Vente" par défaut si cette personne n'a accès qu'à "Carte
  // cadeau" (ou uniquement au registre fiscal).
  const defaultTab = hasPerm(user, "caisse", "voir_vente")
    ? "vente"
    : hasPerm(user, "caisse", "voir_carte_cadeau")
      ? "carte-cadeau"
      : canFiscal ? "fiscal" : "vente";

  const load = async () => {
    const [a, t, cat] = await Promise.all([api.get("/stock"), api.get("/caisse"), api.get("/categories")]);
    setArticles(a.data);
    setTickets(t.data);
    setCategories(cat.data);
    try {
      const { data: pc } = await api.get("/pa/config");
      const ids = t.data.filter((x) => x.type === "facture").map((x) => x.id);
      if (pc.enabled && ids.length) {
        const { data: st } = await api.post("/pa/statuses", { ids });
        setPaStatuses(st);
      }
    } catch { /* PA non disponible : l'écran reste utilisable */ }
  };

  const paBadge = (t) => {
    if (t.pa_provisoire) return <span className="text-xs font-semibold text-amber-600" title="Vente forcée pendant une panne de la plateforme : à régulariser en facture">PROVISOIRE — À RÉGULARISER</span>;
    const st = paStatuses[t.id];
    if (!st?.applicable) return null;
    if (st.status === "rejected") return <span className="text-xs font-semibold text-destructive" title={st.message || ""}>REFUSÉE PAR LA PA</span>;
    if (st.pdf_ready) return <span className="text-xs font-semibold text-emerald-600">PA : {st.status === "accepted" ? "ACCEPTÉE" : "TRANSMISE"}</span>;
    return <span className="text-xs font-semibold text-amber-600" title="Le PDF officiel arrive dès que la plateforme l'a produit">EN ATTENTE DE LA PA</span>;
  };
  const paPdfBlocked = (t) => {
    const st = paStatuses[t.id];
    return !!(st?.applicable && !st.pdf_ready);
  };

  const loadPaymentMethods = async () => {
    try {
      const { data } = await api.get("/admin/payment-methods");
      setPaymentMethods(data.filter((m) => m.enabled));
    } catch (e) {
      setPaymentMethods([]);
    }
  };

  const loadFiscal = async () => {
    if (!canFiscal) return;
    try {
      const [c, v, cu, fs] = await Promise.all([
        api.get("/caisse/clotures"),
        api.get("/caisse/verify-chain"),
        api.get("/caisse/cumuls"),
        api.get("/caisse/fiscal-settings"),
      ]);
      setClotures(c.data);
      setChainStatus(v.data);
      setCumuls(cu.data);
      setComptaEmails(Array.isArray(fs.data?.compta_emails) ? fs.data.compta_emails : []);
      setAnchorEmailSecondaire(fs.data?.fiscal_anchor_email_secondaire || "");
      setTicketPrixAffichage(fs.data?.ticket_prix_affichage || "ttc");
    } catch (e) {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };

  // ---- Verrouillage opérationnel de l'onglet Ventes (voir ventes_lock_utils.py) ----
  // INDÉPENDANT de la clôture fiscale (toujours 00h15, jamais réglable) : un
  // simple réglage d'horaire pour bloquer la vente après l'heure de fermeture,
  // jusqu'à 00h01 le lendemain, avec réouverture forcée par code PIN admin.
  const [ventesLock, setVentesLock] = useState(null); // { enabled, locked, lock_hour, locked_until, force_reopened }
  const [lockHourInput, setLockHourInput] = useState("");
  const [lockHourSaving, setLockHourSaving] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenPin, setReopenPin] = useState("");
  const [reopenSubmitting, setReopenSubmitting] = useState(false);

  const loadVentesLock = async () => {
    try {
      const { data } = await api.get("/caisse/ventes-lock");
      setVentesLock(data);
      setLockHourInput(data.lock_hour || "");
    } catch {
      // silencieux : boutique de travail pas encore sélectionnée
    }
  };

  useEffect(() => {
    loadVentesLock();
    // Revérifie chaque minute : le verrou doit s'activer/se lever tout seul à
    // l'heure prévue sans que l'utilisateur ait besoin de recharger la page.
    const interval = setInterval(loadVentesLock, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveLockHour = async () => {
    setLockHourSaving(true);
    try {
      const { data } = await api.patch("/caisse/ventes-lock-settings", { ventes_lock_hour: lockHourInput });
      setVentesLock(data);
      toast.success(lockHourInput ? "Heure de verrouillage enregistrée" : "Verrouillage désactivé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLockHourSaving(false);
    }
  };

  const submitForceReopen = async () => {
    if (!reopenPin.trim()) { toast.error("Saisissez le code administrateur"); return; }
    setReopenSubmitting(true);
    try {
      const { data } = await api.post("/caisse/ventes-lock/force-reopen", { pin: reopenPin.trim() });
      setVentesLock(data);
      setReopenDialogOpen(false);
      setReopenPin("");
      toast.success("Onglet Ventes rouvert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReopenSubmitting(false);
    }
  };

  const loadSession = async () => {
    try {
      const isMultiple = shopSettings?.caisse_mode === "multiple";
      const numero = isMultiple ? getStoredCaisseNumero() : null;
      // Bug corrigé (demande explicite) : en mode multi-caisses, sans numéro
      // mémorisé (ex. juste après avoir fermé ou quitté SA caisse), on
      // n'interroge plus /session/current du tout — l'appeler sans numéro
      // pouvait renvoyer N'IMPORTE QUELLE AUTRE caisse encore ouverte par un
      // collègue, donnant l'impression de ne jamais être "éjecté" après une
      // fermeture. On considère directement qu'il n'y a pas de session.
      if (isMultiple && !numero) { setCurrentSession(null); return; }
      const { data } = await api.get("/caisse/session/current", numero ? { params: { caisse_numero: numero } } : {});
      setCurrentSession(data || null);
    } catch (e) {
      setCurrentSession(null);
    }
  };

  const loadSessionHistory = async () => {
    try {
      const { data } = await api.get("/caisse/session/history");
      setSessionHistory(data || []);
    } catch (e) {
      setSessionHistory([]);
    }
  };

  // Toutes les caisses actuellement ouvertes pour la boutique (une par numéro,
  // en mode multi-caisses) — alimente le menu "Choisissez la caisse à ouvrir".
  const loadOpenSessions = async () => {
    try {
      const { data } = await api.get("/caisse/session/ouvertes");
      setOpenSessions(data || []);
    } catch (e) {
      setOpenSessions([]);
    }
  };

  // Loi AGEC (art. L541-15-10 code de l'environnement) : depuis le 1er août
  // 2023, le ticket de caisse n'est plus imprimé/remis automatiquement à
  // chaque vente — seulement sur demande du client, sauf activité exemptée
  // (hôtellerie-restauration notamment). On lit ce réglage sur la fiche
  // boutique (voir Administrateurs > Boutiques & Dépôts) pour savoir si
  // l'ouverture automatique du PDF après une vente doit avoir lieu ou non.
  const loadShopSettings = async () => {
    try {
      const { data } = await api.get("/shops");
      const mine = data.find((s) => s.id === user?.effective_shop_id);
      setShopSettings(mine || null);
    } catch (e) {
      setShopSettings(null);
    }
  };

  // Point d'entrée du bouton "Ouvrir la caisse" : en mode caisse unique,
  // comportement historique inchangé (demande directement le fond de
  // départ). En mode multi-caisses, ouvre d'abord le menu de choix de caisse
  // (voir caisseChoiceDialog) — chaque caisse y indique si elle est libre
  // ("Ouvrir") ou déjà ouverte par quelqu'un ("Rejoindre").
  const openSessionFlow = async () => {
    if (shopSettings?.caisse_mode !== "multiple") {
      setSessionOpenDialog(true);
      return;
    }
    await loadOpenSessions();
    setCaisseChoiceDialog(true);
  };

  // Depuis le menu de choix de caisse : clic sur une caisse LIBRE -> mémorise
  // son numéro puis ouvre le dialogue habituel de saisie du fond de départ.
  const pickCaisseToOpen = (numero) => {
    setSelectedCaisseNumero(numero);
    setOpenPin("");
    setShowOpenPinKeypad(false);
    setCaisseChoiceDialog(false);
    setSessionOpenDialog(true);
  };

  // Depuis le menu de choix de caisse : clic sur "Rejoindre" pour une caisse
  // DÉJÀ ouverte par quelqu'un d'autre — aucune saisie de fond de départ,
  // juste un ajout aux participants + au journal côté serveur.
  const joinCaisse = async (session) => {
    try {
      const { data } = await api.post(`/caisse/session/${session.id}/join`);
      setCurrentSession(data);
      setStoredCaisseNumero(data.caisse_numero);
      setCaisseChoiceDialog(false);
      toast.success(`Caisse ${data.caisse_numero} rejointe`);
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Quitte la caisse actuellement ouverte/rejointe SANS la fermer (elle reste
  // utilisable par les collègues encore dessus) — distinct de "Fermer la
  // caisse (comptage)", qui met fin à la session pour de bon.
  const leaveSession = async () => {
    if (!currentSession) return;
    try {
      await api.post(`/caisse/session/${currentSession.id}/leave`);
      setCurrentSession(null);
      clearStoredCaisseNumero();
      toast.success("Vous avez quitté cette caisse");
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openMouvementDialog = (type) => {
    setMouvementType(type);
    setMouvementMontant("");
    setMouvementMotif("");
    setSessionMouvementDialog(true);
  };

  const confirmMouvement = async () => {
    if (!mouvementMontant || Number(mouvementMontant) <= 0) {
      toast.error("Indiquez un montant positif");
      return;
    }
    setMouvementSaving(true);
    try {
      const { data } = await api.post(`/caisse/session/${currentSession.id}/mouvement`, {
        type: mouvementType, montant: Number(mouvementMontant), motif: mouvementMotif.trim(),
      });
      setCurrentSession(data);
      toast.success(mouvementType === "entree" ? "Entrée d'espèces enregistrée" : "Sortie d'espèces enregistrée");
      setSessionMouvementDialog(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMouvementSaving(false);
    }
  };

  // Bouton "Journal" d'une ligne de l'historique des sessions — récupère le
  // détail complet (journal + participants) de cette session précise.
  const openJournal = async (session) => {
    try {
      const { data } = await api.get(`/caisse/session/${session.id}`);
      setJournalDialogSession(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Bouton "Réinitialiser le PIN" d'une session ouverte protégée (onglet
  // Registre fiscal, demande explicite) : un code PIN oublié bloquait
  // définitivement la possibilité de rejoindre cette caisse (voir
  // join_session côté backend) — elle restait fermable, mais plus personne
  // ne pouvait y revenir vendre entre-temps. Réservé à canManageFiscal
  // (même droit que l'annulation de ticket/avoir), confirmation demandée
  // car l'action retire une protection de sécurité.
  const resetSessionPin = async (session) => {
    if (!window.confirm("Réinitialiser le code PIN de cette caisse ? N'importe qui pourra ensuite la rejoindre sans code.")) return;
    try {
      await api.post(`/caisse/session/${session.id}/reset-pin`);
      toast.success("Code PIN réinitialisé");
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openSession = async () => {
    setSessionSaving(true);
    try {
      const { data } = await api.post("/caisse/session/open", {
        fond_ouverture: Number(fondOuverture) || 0,
        caisse_numero: shopSettings?.caisse_mode === "multiple" ? selectedCaisseNumero : 1,
        pin: shopSettings?.caisse_mode === "multiple" && openPin.length === 3 ? openPin : null,
      });
      toast.success(shopSettings?.caisse_mode === "multiple" ? `Caisse ${data.caisse_numero} ouverte` : "Session de caisse ouverte");
      setSessionOpenDialog(false);
      setFondOuverture("");
      setOpenPin("");
      setShowOpenPinKeypad(false);
      setCurrentSession(data);
      setStoredCaisseNumero(data.caisse_numero);
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSessionSaving(false);
    }
  };

  const closeSession = async () => {
    if (fondFermetureReel === "") { toast.error("Indiquez le montant d'espèces compté dans le tiroir"); return; }
    setSessionSaving(true);
    try {
      const { data } = await api.post(`/caisse/session/${currentSession.id}/preview-close`, {
        fond_fermeture_reel: Number(fondFermetureReel) || 0,
      });
      setClosePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSessionSaving(false);
    }
  };

  const confirmCloseSession = async () => {
    const hasEcart = Math.abs(closePreview.ecart) >= 0.01;
    if (hasEcart && !sessionNotes.trim()) {
      toast.error("Merci d'indiquer d'où vous pensez que vient l'écart avant de confirmer");
      return;
    }
    setSessionSaving(true);
    try {
      const { data } = await api.post(`/caisse/session/${currentSession.id}/close`, {
        fond_fermeture_reel: Number(fondFermetureReel) || 0,
        notes: sessionNotes,
      });
      const ecart = data.ecart || 0;
      if (Math.abs(ecart) < 0.01) toast.success("Caisse fermée : aucun écart constaté");
      else toast.warning(`Caisse fermée : écart de ${ecart > 0 ? "+" : ""}${ecart.toFixed(2)} €`);
      setSessionCloseDialog(false);
      setClosePreview(null);
      setFondFermetureReel("");
      setSessionNotes("");
      clearStoredCaisseNumero();
      loadSession();
      loadSessionHistory();
      loadOpenSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSessionSaving(false);
    }
  };

  useEffect(() => { load(); loadFiscal(); loadPaymentMethods(); loadSession(); loadSessionHistory(); loadShopSettings(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recharge la session dès que le réglage caisse_mode de la boutique est
  // connu (chargé en parallèle ci-dessus, potentiellement après le premier
  // appel de loadSession()) : en mode multi-caisses, loadSession() a besoin
  // de savoir CE réglage pour interroger /session/current avec le bon numéro
  // de caisse mémorisé localement (voir getStoredCaisseNumero).
  useEffect(() => {
    if (shopSettings) { loadSession(); loadOpenSessions(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettings?.caisse_mode]);

  useEffect(() => {
    if (shopSettings?.vente_mode === "par_table") loadTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettings?.vente_mode]);

  // ---- Vue "Rendez-vous du jour" de l'onglet Vente (modèle "rendez_vous") ----
  // Grille visuelle (colonnes = vendeurs, lignes = créneaux) des rendez-vous
  // du jour déjà pris depuis l'onglet "Prise de rdv" (même agenda partagé,
  // voir rendezvous_routes.py) — cliquer un rendez-vous ajoute directement
  // ses prestations au panier, sans empêcher d'ajouter des articles à la
  // main en dessous (recherche/grille tactile normales, inchangées).
  const [rdvDuJour, setRdvDuJour] = useState([]);
  const [rdvVendeurs, setRdvVendeurs] = useState([]);
  const [selectedRdvInfo, setSelectedRdvInfo] = useState(null);
  // Rendez-vous dont une prestation a été ajoutée au panier en cours — marqués
  // "termine" (vert partout) à la clôture de LA vente qui les contient (voir
  // plus bas, dans create_ticket côté succès).
  const [pendingRdvIds, setPendingRdvIds] = useState([]);
  // Horloge vive : un rendez-vous passe tout seul en rouge ("en retard") dès
  // que l'heure de fin prévue est dépassée — voir calendarUtils.js > useNow.
  const rdvNow = useNow();

  const loadRdvDuJour = async () => {
    const now = new Date();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    try {
      const { data } = await api.get("/rendez-vous", { params: { date_from: dayStart.toISOString(), date_to: dayEnd.toISOString() } });
      setRdvDuJour(data || []);
    } catch (e) { /* silencieux : la vue vente reste utilisable sans le planning */ }
  };

  useEffect(() => {
    if (shopSettings?.vente_mode !== "rendez_vous") return;
    loadRdvDuJour();
    api.get("/users").then((r) => setRdvVendeurs((r.data || []).filter((u) => u.shop_id === user?.effective_shop_id))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSettings?.vente_mode, user?.effective_shop_id]);

  // Les vendeurs affichés en colonnes : ceux de la boutique qui ont au moins
  // un rendez-vous aujourd'hui, dans l'ordre de leur premier rendez-vous —
  // évite d'afficher des colonnes vides pour du personnel sans planning.
  const rdvColonnesVendeurs = (() => {
    const idsAvecRdv = [];
    for (const r of rdvDuJour) {
      if (r.vendeur_id && !idsAvecRdv.includes(r.vendeur_id)) idsAvecRdv.push(r.vendeur_id);
    }
    const parId = Object.fromEntries(rdvVendeurs.map((v) => [v.id, v]));
    return idsAvecRdv.map((id) => parId[id] || { id, prenom: rdvDuJour.find((r) => r.vendeur_id === id)?.vendeur_nom || "?", nom: "" });
  })();

  // Créneaux de 30 minutes couvrant tous les rendez-vous du jour (du premier
  // arrondi à la demi-heure inférieure, au dernier arrondi à la demi-heure
  // supérieure) — évite une grille inutilement grande en dehors des heures
  // d'ouverture réelles.
  const rdvCreneaux = (() => {
    if (rdvDuJour.length === 0) return [];
    let min = Infinity, max = -Infinity;
    for (const r of rdvDuJour) {
      const start = new Date(r.date_heure).getTime();
      const end = start + (Number(r.duree_minutes) || 30) * 60000;
      if (start < min) min = start;
      if (end > max) max = end;
    }
    const STEP = 30 * 60000;
    const startSlot = Math.floor(min / STEP) * STEP;
    const endSlot = Math.ceil(max / STEP) * STEP;
    const slots = [];
    for (let t = startSlot; t < endSlot; t += STEP) slots.push(new Date(t));
    return slots;
  })();

  const rdvAuCreneau = (vendeurId, slot) => {
    const slotStart = slot.getTime();
    const slotEnd = slotStart + 30 * 60000;
    return rdvDuJour.find((r) => {
      if (r.vendeur_id !== vendeurId) return false;
      const start = new Date(r.date_heure).getTime();
      return start >= slotStart && start < slotEnd;
    });
  };

  // Couleur stable par type de prestation (même prestation = même couleur,
  // quel que soit le vendeur ou l'horaire), pour repérer rapidement les
  // rendez-vous similaires dans la grille — sauf "terminé" (vente clôturée,
  // voir addRdvToCart/succès de create_ticket) ou "en retard" (heure de fin
  // prévue dépassée sans clôture), qui priment toujours sur la couleur par
  // prestation : vert / rouge partout où l'agenda partagé s'affiche (Vente,
  // Prise de rdv, tableau de bord — voir lib/calendarUtils.js).
  const RDV_PALETTE = [
    { bg: "bg-sky-500/15", text: "text-sky-700 dark:text-sky-400", ring: "ring-sky-500/50" },
    { bg: "bg-amber-500/15", text: "text-amber-700 dark:text-amber-400", ring: "ring-amber-500/50" },
    { bg: "bg-rose-500/15", text: "text-rose-700 dark:text-rose-400", ring: "ring-rose-500/50" },
    { bg: "bg-violet-500/15", text: "text-violet-700 dark:text-violet-400", ring: "ring-violet-500/50" },
  ];
  const RDV_STATUS_COLORS = {
    termine: { bg: "bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-400", ring: "ring-emerald-500/60" },
    en_retard: { bg: "bg-red-500/20", text: "text-red-700 dark:text-red-400", ring: "ring-red-500/60" },
  };
  const couleurRdv = (r) => {
    const status = rdvVisualStatus(r, { now: rdvNow });
    if (RDV_STATUS_COLORS[status]) return RDV_STATUS_COLORS[status];
    const key = (r.prestations?.[0]?.nom || r.titre || "").toLowerCase();
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    return RDV_PALETTE[hash % RDV_PALETTE.length];
  };

  // Ajoute les prestations d'un rendez-vous au panier — indépendant de tout
  // ajout manuel d'article, qui reste possible juste en dessous (recherche
  // ou grille tactile) pour compléter la même vente.
  const addRdvToCart = (rdv) => {
    const prestations = rdv.prestations && rdv.prestations.length > 0
      ? rdv.prestations
      : (rdv.prix_ttc != null ? [{ article_id: null, nom: rdv.titre, duree_minutes: rdv.duree_minutes || 0, prix_ttc: rdv.prix_ttc }] : []);
    if (rdv.statut === "termine") {
      toast.error("Ce rendez-vous a déjà été encaissé");
      return;
    }
    if (prestations.length === 0) {
      toast.error("Ce rendez-vous n'a pas de prestation chiffrée — ajoutez les articles à facturer manuellement");
      return;
    }
    setPendingRdvIds((ids) => (ids.includes(rdv.id) ? ids : [...ids, rdv.id]));
    setCart((c) => {
      const nouvellesLignes = prestations.map((p) => {
        const articleLie = p.article_id ? articles.find((a) => a.id === p.article_id) : null;
        const tvaPercent = articleLie?.tva_percent ?? 20;
        const prixTtc = Number(p.prix_ttc) || 0;
        const prixHt = prixTtc / (1 + tvaPercent / 100);
        return {
          type: articleLie ? "article" : "prestation",
          article_id: articleLie ? articleLie.id : undefined,
          nom: p.nom, prix_unitaire: prixHt, prix_origine: prixHt, quantite: 1,
          remise_label: null, tva_percent: tvaPercent,
          prix_achat_ht: articleLie?.prix_achat || 0, eco_participation_ht: articleLie?.eco_participation_ht || 0,
        };
      });
      return [...c, ...nouvellesLignes];
    });
    setSelectedRdvInfo({
      heure: new Date(rdv.date_heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      label: rdv.vendeur_nom || rdv.client_nom || rdv.titre,
    });
    toast.success("Rendez-vous ajouté au panier");
  };

  // Sauvegarde automatique (avec un léger délai pour ne pas spammer le
  // serveur à chaque clic) de l'état du panier sur la table active — la
  // table reste exactement dans l'état laissé si on change d'écran ou de
  // vendeur avant l'encaissement.
  useEffect(() => {
    if (!activeTableId) return;
    const t = setTimeout(() => {
      api.patch(`/table-sessions/${activeTableId}`, { lignes: cart }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, activeTableId]);

  useEffect(() => {
    if (!clientSearch.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: clientSearch } }).then((r) => setClientResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const filteredArticles = articles
    .filter((a) => showOutOfStock || a.type === "formule" || a.type === "service" || a.stock_infini || (a.quantite || 0) > 0)
    .filter((a) => {
      const q = articleSearch.trim().toLowerCase();
      if (!q) return true;
      return (a.nom || "").toLowerCase().includes(q) || (a.categorie || "").toLowerCase().includes(q) || (a.code || "").toLowerCase().includes(q) || (a.ean || "").toLowerCase().includes(q);
    });
  const outOfStockCount = articles.filter((a) => a.type !== "formule" && a.type !== "service" && !a.stock_infini && (a.quantite || 0) <= 0).length;

  // Modèle "Grille tactile par catégorie" (voir Administrateurs > Boutiques
  // > Modèle de l'onglet Vente) — gros boutons visuels groupés par
  // catégorie, adaptés à un commerce avec un nombre limité de références
  // fixes (boulangerie, snack...) plutôt qu'une recherche au clavier.
  const [grilleCategorie, setGrilleCategorie] = useState(null);
  const articlesEnStock = articles.filter((a) => showOutOfStock || a.type === "formule" || a.type === "service" || a.stock_infini || (a.quantite || 0) > 0);
  const categoriesAvecArticles = categories.filter((c) => articlesEnStock.some((a) => a.categorie === c.nom));
  const grilleCategorieActive = grilleCategorie && categoriesAvecArticles.some((c) => c.nom === grilleCategorie)
    ? grilleCategorie
    : categoriesAvecArticles[0]?.nom || null;
  const articlesDeLaCategorie = articlesEnStock.filter((a) => a.categorie === grilleCategorieActive);

  // Modèle "Sur rendez-vous" : sélecteur "Prestation" / "Produit" avec sa
  // propre recherche (voir plus bas dans le rendu) — Prestation = tous les
  // articles de type "service", Produit = tout le reste.
  const venteRdvArticlesFiltres = articles
    .filter((a) => (venteRdvCategorie === "prestation" ? a.type === "service" : a.type !== "service"))
    .filter((a) => {
      const q = venteRdvArticleSearch.trim().toLowerCase();
      if (!q) return true;
      return (a.nom || "").toLowerCase().includes(q) || (a.categorie || "").toLowerCase().includes(q);
    });

  const renderTicketRow = (t, prefix = "history") => (
    <div key={t.id} className={`flex flex-wrap items-center justify-between text-sm py-2 px-3 rounded-lg border bg-card gap-2 ${t.annule ? "border-destructive/40 opacity-70" : "border-border"}`} data-testid={`${prefix}-row-${t.id}`}>
      <div className="min-w-0">
        <span data-testid={`${prefix}-numero-${t.id}`} className="flex items-center gap-1.5 truncate">
          {t.numero} — {t.type}
          <Lock size={11} className="text-muted-foreground" title="Document verrouillé (inaltérable)" />
          {t.annule && <span className="text-xs font-semibold text-destructive">ANNULÉ</span>}
          {t.avoir_id && <span className="text-xs font-semibold text-amber-600">AVOIR ÉMIS</span>}
          {t.duplicata && <span className="text-xs font-semibold text-amber-600">DUPLICATA</span>}
          {t.type === "avoir" && <span className="text-xs font-semibold text-amber-600">AVOIR</span>}
          {paBadge(t)}
        </span>
        {t.ticket_source_numero && (
          <span className="text-xs text-muted-foreground truncate block">Établie sur le ticket {t.ticket_source_numero}</span>
        )}
        <span className="text-xs text-muted-foreground truncate block">{t.caisse_numero != null ? `Caisse ${t.caisse_numero} — ` : ""}{t.vendeur_nom}{prefix === "anterior" && t.created_at ? ` — ${new Date(t.created_at).toLocaleDateString("fr-FR")}` : ""}</span>
        {t.is_shared_to_me && (
          <span className="text-xs text-muted-foreground truncate block" data-testid={`${prefix}-shared-badge-${t.id}`}>
            Partagé par {t.shared_by_label} ({t.share_mode === "write" ? "écriture" : "lecture"})
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0 ml-auto">
        <span className="font-medium">{t.total_ttc?.toFixed(2)} €</span>
        <div className="flex items-center gap-2">
          {paPdfBlocked(t) ? (
            <span title="PDF officiel de la plateforme agréée pas encore disponible" className="text-muted-foreground/40 flex items-center gap-2">
              <Eye size={15} /><FileDown size={15} />
            </span>
          ) : (
            <>
              <a href={`${api.defaults.baseURL}/caisse/${t.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`${prefix}-view-${t.id}`} title="Voir">
                <Eye size={15} className="text-muted-foreground hover:text-foreground" />
              </a>
              <a href={`${api.defaults.baseURL}/caisse/${t.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`${prefix}-pdf-${t.id}`} title="Télécharger PDF">
                <FileDown size={15} className="text-primary" />
              </a>
            </>
          )}
          <button data-testid={`${prefix}-mail-${t.id}`} onClick={() => openMailDialog(t)} title="Envoyer par e-mail">
            <Mail size={15} className="text-muted-foreground hover:text-foreground" />
          </button>
          {hasPerm(user, "partage_document") && !t.is_shared_to_me && (
            <button data-testid={`${prefix}-share-${t.id}`} onClick={() => setShareItem(t.id)} title="Partager">
              <Share2 size={14} />
            </button>
          )}
          {canFacturer(t) && (
            <button data-testid={`${prefix}-facturer-${t.id}`} onClick={() => openFacturer(t)} title={t.pa_provisoire ? "Régulariser : émettre la facture (envoi à la plateforme agréée)" : "Émettre un duplicata facture de ce ticket"}>
              <FileBadge size={14} className="text-blue-600" />
            </button>
          )}
          {canDoFiscalActions(t) && (
            <>
              <button data-testid={`${prefix}-avoir-${t.id}`} onClick={() => openAvoir(t)} title="Émettre un avoir (correction tracée)">
                <Undo2 size={14} className="text-amber-600" />
              </button>
              {canAnnuler(t) ? (
                <button data-testid={`${prefix}-annuler-${t.id}`} onClick={() => openAnnuler(t)} title="Annuler (motif obligatoire, document conservé)" className="text-destructive">
                  <Ban size={14} />
                </button>
              ) : (
                <span title="Déjà inclus dans une clôture : utilisez l'avoir pour corriger" className="text-muted-foreground/40">
                  <Ban size={14} />
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  // "Tickets du jour" affichés en ligne dans l'onglet Vente — pour tout
  // l'historique, voir le bouton "Tickets antérieurs" (filteredAnteriorTickets
  // ci-dessous), qui n'est lui pas limité à aujourd'hui.
  const isToday = (isoDate) => {
    if (!isoDate) return false;
    const d = new Date(isoDate);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  };
  const filteredHistory = tickets
    .filter((t) => isToday(t.created_at))
    .filter((t) => {
      const q = historySearch.trim().toLowerCase();
      if (!q) return true;
      return (t.numero || "").toLowerCase().includes(q) || (t.vendeur_nom || "").toLowerCase().includes(q) || (t.client_info?.nom || "").toLowerCase().includes(q);
    })
    .slice(0, 30);

  const filteredAnteriorTickets = tickets
    .filter((t) => {
      const q = anteriorSearch.trim().toLowerCase();
      if (!q) return true;
      return (t.numero || "").toLowerCase().includes(q) || (t.vendeur_nom || "").toLowerCase().includes(q) || (t.client_info?.nom || "").toLowerCase().includes(q);
    })
    .filter((t) => {
      if (!anteriorDate) return true;
      if (!t.created_at) return false;
      return new Date(t.created_at).toISOString().slice(0, 10) === anteriorDate;
    });

  const addArticle = (article, poidsKg = null) => {
    setCart((c) => {
      // Un article "vendu au poids" (voir Administrateurs > Boutiques >
      // Modèle de l'onglet Vente) n'est jamais fusionné avec une ligne
      // existante : chaque pesée est sa propre ligne, avec son propre poids
      // affiché sur le ticket — fusionner deux pesées différentes ferait
      // perdre le détail de chacune.
      if (!poidsKg) {
        const existing = c.find((i) => i.article_id === article.id);
        if (existing) return c.map((i) => (i.article_id === article.id ? { ...i, quantite: i.quantite + 1 } : i));
      }
      // Le prix de l'article en stock est en H.T ; la TVA appliquée est celle
      // configurée sur l'article (par défaut 20%), plus modifiable à la caisse.
      const prix = article.prix || 0;
      const tvaPercent = article.tva_percent ?? 20;
      // Vente au poids : `prix` est un prix AU KG (H.T) — la quantité de la
      // ligne devient directement le poids en kg saisi, le nom précise le
      // poids pour rester clair sur le ticket imprimé.
      const nom = poidsKg ? `${article.nom} (${poidsKg} ${uniteDe(article)})` : article.nom;
      const quantite = poidsKg || 1;
      // Le prix d'achat fournisseur (H.T) est figé sur la ligne au moment de la
      // vente : il sert au calcul du bénéfice dans les Statistiques et ne doit
      // jamais apparaître à l'écran ni sur un document imprimé.
      return [...c, { type: "article", article_id: article.id, nom, prix_unitaire: prix, prix_origine: prix, quantite, remise_label: null, tva_percent: tvaPercent, prix_achat_ht: article.prix_achat || 0, eco_participation_ht: article.eco_participation_ht || 0 }];
    });
  };

  const confirmAcompte = () => {
    const ttcMontant = parseFloat(String(acompteMontant).replace(",", "."));
    if (!ttcMontant || ttcMontant <= 0) { toast.error("Indiquez un montant d'acompte valide"); return; }
    if (ttcMontant > (acompteDialog?.reste ?? Infinity) + 0.005) { toast.error(`Maximum : ${Number(acompteDialog.reste).toFixed(2)} € (reste à percevoir)`); return; }
    const tva = acompteDialog.tva_percent ?? 20;
    setCart([{
      type: "prestation", article_id: null, nom: `Acompte devis n°${acompteDialog.numero}`,
      prix_unitaire: ttcMontant / (1 + tva / 100), prix_origine: null, quantite: 1, remise_label: null,
      tva_percent: tva, prix_achat_ht: 0, eco_participation_ht: 0, verrouille: true,
    }]);
    setAcompteDialog(null);
  };

  // Menus et formules (modèle "menus_formules") : ajoute chaque composant
  // comme une ligne de panier À PART ENTIÈRE (même article_id qu'une vente
  // normale de ce composant, donc même décrémentation de stock), mais avec
  // un prix calculé au PRORATA du prix fixe de la formule plutôt que le
  // prix normal du composant — la somme des lignes reste égale au prix
  // affiché au client, tout en respectant le taux de TVA propre à chacune.
  const addFormule = (formule) => {
    const composants = formule.composants || [];
    if (composants.length === 0) return;
    const totalReference = composants.reduce((sum, c) => sum + c.prix_ttc_reference * c.quantite, 0);
    const prixFormule = formule.prix_ttc ?? formule.prix ?? 0;
    // Identifiant unique à CETTE formule ajoutée au panier (pas à la
    // formule elle-même, qui peut être ajoutée plusieurs fois) — sert à
    // regrouper ses composants sous une même ligne d'en-tête "1x Menu
    // (nom)" sur le ticket imprimé (voir pdf_utils.py), sans jamais changer
    // le calcul du prix ni de la TVA de chaque composant, inchangés.
    const groupId = `menu-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setCart((c) => {
      const nouvellesLignes = composants.map((comp) => {
        const partTtc = totalReference > 0 ? ((comp.prix_ttc_reference * comp.quantite) / totalReference) * prixFormule : 0;
        const prixUnitaireTtc = comp.quantite > 0 ? partTtc / comp.quantite : 0;
        const prixUnitaireHt = prixUnitaireTtc / (1 + (comp.tva_percent || 0) / 100);
        return {
          type: "article", article_id: comp.article_id, nom: comp.nom,
          prix_unitaire: prixUnitaireHt, prix_origine: prixUnitaireHt, quantite: comp.quantite,
          remise_label: null, tva_percent: comp.tva_percent, prix_achat_ht: 0, eco_participation_ht: 0,
          menu_group_id: groupId, menu_nom: formule.nom,
        };
      });
      return [...c, ...nouvellesLignes];
    });
  };

  // Vente au poids : intercepte le clic sur un article marqué "vendu au
  // poids" pour demander le poids avant de l'ajouter au panier, plutôt que
  // d'ajouter directement 1 unité comme pour un article classique.
  const [poidsDialogArticle, setPoidsDialogArticle] = useState(null);
  const [poidsInput, setPoidsInput] = useState("");
  // Service sur rendez-vous (modèle "rendez_vous") : intercepte le clic sur
  // un article de type "service" pour proposer soit une vente immédiate
  // (ajout direct au panier), soit la prise d'un rendez-vous — dans ce
  // second cas, on ouvre le MÊME formulaire complet que l'onglet "Prise de
  // rdv" (RdvFormDialog, voir PriseRdvTab.jsx), pré-rempli avec cette
  // prestation, plutôt qu'un mini-formulaire séparé et limité.
  const [serviceDialogArticle, setServiceDialogArticle] = useState(null);
  // Article à l'origine de l'ouverture du formulaire de rendez-vous complet
  // (non nul => RdvFormDialog ouvert en pré-remplissage) — indépendant de
  // rdvPresetArticle : ajouter le service au panier ET prendre un rendez-vous
  // restent deux actions séparées, jamais combinées automatiquement.
  const [rdvPresetArticle, setRdvPresetArticle] = useState(null);

  const handleArticleClick = (article) => {
    if (article.type === "formule") {
      addFormule(article);
    } else if (article.type === "service") {
      setServiceDialogArticle(article);
    } else if (article.vendu_au_poids) {
      setPoidsDialogArticle(article);
      setPoidsInput("");
    } else {
      addArticle(article);
    }
  };

  const confirmServiceSansRdv = () => {
    addArticle(serviceDialogArticle);
    setServiceDialogArticle(null);
  };

  const ouvrirRdvDepuisService = () => {
    setRdvPresetArticle(serviceDialogArticle);
    setServiceDialogArticle(null);
  };

  const confirmPoids = () => {
    const poids = parseFloat((poidsInput || "").replace(",", "."));
    if (!poids || poids <= 0) {
      toast.error("Indiquez un poids valide");
      return;
    }
    addArticle(poidsDialogArticle, Math.round(poids * 1000) / 1000);
    setPoidsDialogArticle(null);
  };

  // ---- Douchette (lecteur code-barres USB/Bluetooth, qui "tape" le code puis Entrée) ----
  // Le code est cherché dans l'EAN des articles (l'EAN vaut l'ID article quand il
  // n'est pas renseigné). Un seul article trouvé -> ajout direct au panier ;
  // plusieurs -> choix du produit.
  const [scanChoix, setScanChoix] = useState(null); // { code, articles }
  const disponible = (a) => a.type === "formule" || a.type === "service" || a.stock_infini || (a.quantite || 0) > 0;
  const handleScan = (raw) => {
    const code = String(raw || "").trim().toLowerCase();
    if (!code) return;
    const trouves = articles.filter((a) => String(a.ean || a.code || "").trim().toLowerCase() === code);
    if (trouves.length === 0) { toast.error(`Aucun article avec le code ${raw}`); return; }
    const dispo = trouves.filter(disponible);
    if (dispo.length === 0) { toast.error(`${trouves[0].nom} : en rupture de stock`); return; }
    setArticleSearch("");
    if (dispo.length === 1) { handleArticleClick(dispo[0]); toast.success(`${dispo[0].nom} ajouté`); return; }
    setScanChoix({ code: raw, articles: dispo });
  };
  useEffect(() => {
    if (!currentSession) return;
    let buf = "", last = 0, fast = 0;
    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const inField = t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
      const isArticleSearch = t?.dataset?.testid === "caisse-article-search";
      if (document.querySelector('[role="dialog"]') && !document.querySelector('[data-testid="scan-choix-dialog"]')) { buf = ""; return; }
      if (inField && !isArticleSearch) { buf = ""; return; }
      const now = Date.now();
      if (e.key === "Enter") {
        const code = buf; const rapide = buf.length >= 3 && fast >= buf.length - 1; buf = ""; fast = 0;
        // Frappe rapide = douchette ; ou saisie manuelle dans la recherche qui correspond EXACTEMENT à un code.
        if (rapide || (isArticleSearch && code.length >= 3)) {
          const exact = articles.some((a) => String(a.ean || a.code || "").trim().toLowerCase() === code.trim().toLowerCase());
          if (rapide || exact) { e.preventDefault(); handleScan(code); }
        } else if (isArticleSearch) {
          const q = (t.value || "").trim();
          if (q && articles.some((a) => String(a.ean || a.code || "").trim().toLowerCase() === q.toLowerCase())) { e.preventDefault(); handleScan(q); }
        }
        return;
      }
      if (e.key.length === 1) {
        if (now - last > 80) { buf = ""; fast = 0; }
        else fast += 1;
        buf += e.key; last = now;
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, articles, cart]);

  // Pavé numérique tactile pour saisir le poids (même logique que "Fixer le
  // prix" côté Stock) — jusqu'ici il fallait obligatoirement taper le poids
  // au clavier physique, ce qui ne convient pas sur une tablette en caisse.
  const poidsAppuyer = (touche) => {
    if (touche === "back") {
      setPoidsInput((v) => v.slice(0, -1));
    } else if (touche === ".") {
      setPoidsInput((v) => (v.includes(".") ? v : (v || "0") + "."));
    } else {
      setPoidsInput((v) => (v === "0" ? touche : v + touche));
    }
  };

  // Clavier physique toujours utilisable en parallèle (chiffres, virgule ou
  // point pour la décimale, retour arrière, Entrée pour valider) — actif
  // uniquement pendant que ce dialogue précis est ouvert.
  useEffect(() => {
    if (!poidsDialogArticle) return;
    const onKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key)) { poidsAppuyer(e.key); return; }
      if (e.key === "," || e.key === ".") { poidsAppuyer("."); return; }
      if (e.key === "Backspace") { poidsAppuyer("back"); return; }
      if (e.key === "Enter") { confirmPoids(); }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poidsDialogArticle, poidsInput]);

  const addPrestation = () => {
    if (!prestationName || !prestationPrice) return;
    // Pour une prestation libre, le prix saisi est en TTC : on le convertit en H.T
    // à partir du taux de TVA choisi, pour rester cohérent avec les articles (dont
    // le prix stocké dans le panier est toujours en H.T).
    const ttc = Number(prestationPrice);
    if (!Number.isFinite(ttc) || ttc < 0) {
      toast.error("Le prix de la prestation doit être un nombre positif");
      return;
    }
    const tvaPercent = Number(prestationTva) || 0;
    const ht = ttc / (1 + tvaPercent / 100);
    setCart((c) => [...c, { type: "prestation", nom: prestationName, prix_unitaire: ht, prix_origine: ht, quantite: 1, remise_label: null, tva_percent: tvaPercent }]);
    setPrestationName(""); setPrestationPrice(""); setPrestationTva(20);
  };

  const updateQty = (idx, delta) => {
    setCart((c) => c.map((item, i) => (i === idx ? { ...item, quantite: Math.max(1, item.quantite + delta) } : item)));
  };

  const removeItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  // Le panier stocke toujours le prix H.T en interne (nécessaire pour la
  // ventilation de TVA et la conformité du ticket/facture), mais on affiche et on
  // saisit les prix en TTC à l'écran — c'est ce que voit le client.
  const toTtc = (item) => (item.prix_unitaire || 0) * (1 + (item.tva_percent || 0) / 100);
  const fromTtc = (ttcVal, tvaPercent) => ttcVal / (1 + (tvaPercent || 0) / 100);

  // Modification directe du prix d'une ligne du panier — évite de devoir aller
  // changer le prix de l'article dans le stock pour une vente ponctuelle.
  const openEditPrice = (idx) => {
    setEditPriceIdx(idx);
    setEditPriceValue(toTtc(cart[idx]).toFixed(2));
  };

  const confirmEditPrice = () => {
    const val = Number(editPriceValue.replace(",", "."));
    if (isNaN(val) || val < 0) { toast.error("Prix invalide"); return; }
    setCart((c) => c.map((item, i) => {
      if (i !== editPriceIdx) return item;
      const ht = fromTtc(val, item.tva_percent);
      return { ...item, prix_unitaire: ht, prix_origine: ht, remise_label: null };
    }));
    setEditPriceIdx(null);
  };

  // Réduction (pourcentage ou montant) appliquée sur le prix d'origine de la ligne —
  // rejouable sans effet cumulatif puisqu'elle repart toujours de prix_origine.
  const openDiscount = (idx) => {
    setDiscountIdx(idx);
    setDiscountType("percent");
    setDiscountValue("");
  };

  const discountPreview = () => {
    if (discountIdx === null) return null;
    const item = cart[discountIdx];
    const baseTtc = toTtc({ prix_unitaire: item?.prix_origine ?? item?.prix_unitaire ?? 0, tva_percent: item?.tva_percent });
    const val = Number(discountValue.replace(",", "."));
    if (isNaN(val) || val < 0) return null;
    const result = discountType === "percent" ? baseTtc * (1 - val / 100) : baseTtc - val;
    return Math.max(0, result);
  };

  const confirmDiscount = () => {
    const val = Number(discountValue.replace(",", "."));
    if (isNaN(val) || val <= 0) { toast.error("Valeur de réduction invalide"); return; }
    setCart((c) => c.map((item, i) => {
      if (i !== discountIdx) return item;
      const baseHt = item.prix_origine ?? item.prix_unitaire;
      const baseTtc = toTtc({ prix_unitaire: baseHt, tva_percent: item.tva_percent });
      const newPriceTtc = Math.max(0, discountType === "percent" ? baseTtc * (1 - val / 100) : baseTtc - val);
      const newPriceHt = fromTtc(newPriceTtc, item.tva_percent);
      const label = discountType === "percent" ? `-${val}%` : `-${val.toFixed(2)} €`;
      return { ...item, prix_unitaire: newPriceHt, remise_label: label };
    }));
    setDiscountIdx(null);
  };

  // Ventilation du panier par taux de TVA — un article à 20% et un article à 10%
  // forment deux groupes distincts, chacun avec son propre montant de TVA.
  const tvaGroups = (() => {
    const map = new Map();
    for (const item of cart) {
      const rate = item.tva_percent ?? 20;
      const lineHt = item.prix_unitaire * item.quantite;
      const g = map.get(rate) || { tva_percent: rate, base_ht: 0, montant_tva: 0, ttc_exact: 0 };
      g.base_ht += lineHt;
      g.ttc_exact += lineHt * (1 + rate / 100);
      map.set(rate, g);
    }
    // Même règle d'arrondi que le serveur (montants_utils.py) : par taux, HT et TTC arrondis au centime,
    // TVA = TTC − HT — le total affiché est exactement celui qui sera encaissé.
    const r2 = (x) => Math.sign(x) * Math.round((Math.abs(x) + 1e-9) * 100) / 100;
    return [...map.values()].map((g) => {
      const base = r2(g.base_ht); const t = r2(g.ttc_exact);
      return { tva_percent: g.tva_percent, base_ht: base, montant_tva: Math.round((t - base) * 100) / 100, ttc: t };
    }).sort((a, b) => b.tva_percent - a.tva_percent);
  })();
  const tvaAmount = tvaGroups.reduce((s, g) => s + g.montant_tva, 0);
  const ttc = tvaGroups.reduce((s, g) => s + g.ttc, 0);
  const ht = tvaGroups.reduce((s, g) => s + g.base_ht, 0);

  // Réduction fidélité (aperçu local) : le montant appliqué est TOUJOURS
  // celui du palier choisi (jamais un calcul par taux libre) — le serveur
  // revalide indépendamment ce même palier à la création du ticket (voir
  // fidelite_utils.find_palier), cet aperçu ne sert qu'à informer le vendeur
  // avant validation du total qu'il devra réellement faire payer.
  const selectedPalier = fidelite?.active && selectedPalierPoints
    ? (fidelite.paliers_cartes_cadeaux || []).find((p) => p.points === Number(selectedPalierPoints))
    : null;
  const fideliteDiscountAmount = selectedPalier ? Math.min(ttc, selectedPalier.montant) : 0;
  const ttcEffective = Math.round((ttc - fideliteDiscountAmount) * 100) / 100;

  // Le panier regroupé visuellement par taux de TVA (les articles d'un même taux
  // ensemble, ceux d'un autre taux à part), avec l'index d'origine conservé pour
  // que les actions (modifier prix, réduction, quantité...) restent correctes.
  const cartGroupedByTva = (() => {
    const order = tvaGroups.map((g) => g.tva_percent);
    return order.map((rate) => ({
      tva_percent: rate,
      entries: cart
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => (item.tva_percent ?? 20) === rate),
    }));
  })();

  const shopId = user?.effective_shop_id;

  // Montant déjà réglé (hors rendu de monnaie) et reste à payer, recalculés à
  // chaque ajout/suppression de paiement.
  const totalRegle = payments.reduce((s, p) => s + (p.montant - p.rendu_monnaie), 0);
  const resteAPayer = Math.max(0, Math.round((ttcEffective - totalRegle) * 100) / 100);

  const openPaymentDialog = () => {
    if (cart.length === 0) return;
    setLastCreatedDoc(null);
    if (paymentMethods.length === 0) {
      // Aucun moyen de paiement configuré/actif : on garde l'ancien comportement
      // (validation directe, sans détail d'encaissement).
      validate([]);
      return;
    }
    setPayments([]);
    setMontantInput(ttcEffective.toFixed(2));
    setCashCeilingBlocked(false);
    setNonResidentIdRef("");
    setPaymentDialogOpen(true);
  };

  const applyPayment = (method) => {
    const montant = Number(String(montantInput).replace(",", "."));
    if (isNaN(montant) || montant <= 0) { toast.error("Montant invalide"); return; }
    if (method.requires_cheque_info) {
      setChequePending({ montant, method });
      setChequeNumero("");
      setChequeNom("");
      return;
    }
    if (method.requires_carte_cadeau_code) {
      setCarteCadeauPending({ montant, method });
      setCarteCadeauCodeInput("");
      setCarteCadeauVerifyResult(null);
      return;
    }
    addPaymentEntry(method, montant);
  };

  const addPaymentEntry = (method, montant, extra = {}) => {
    let applied = montant;
    let rendu = 0;
    if (method.key === "espece") {
      // Seules les espèces peuvent donner lieu à un rendu de monnaie.
      applied = Math.min(montant, resteAPayer);
      rendu = Math.round((montant - applied) * 100) / 100;
    } else if (montant > resteAPayer + 0.005) {
      toast.error(`Le montant dépasse le reste à payer (${resteAPayer.toFixed(2)} €) pour ce moyen de paiement`);
      return;
    }
    const entry = {
      mode: method.key,
      label: method.label,
      montant,
      rendu_monnaie: rendu,
      cheque_numero: extra.numero || null,
      cheque_nom: extra.nom || null,
      carte_cadeau_code: extra.carteCadeauCode || null,
    };
    setPayments((p) => [...p, entry]);
    const newReste = Math.max(0, Math.round((resteAPayer - applied) * 100) / 100);
    setMontantInput(newReste > 0 ? newReste.toFixed(2) : "");
    setChequePending(null);
    setCarteCadeauPending(null);
  };

  const confirmChequePayment = () => {
    if (!chequeNumero.trim()) { toast.error("Le n° de chèque est obligatoire"); return; }
    if (!chequeNom.trim()) { toast.error("Le nom du titulaire est obligatoire"); return; }
    addPaymentEntry(chequePending.method, chequePending.montant, { numero: chequeNumero.trim(), nom: chequeNom.trim() });
  };

  // ---- Paiement par carte cadeau (voir carte_cadeau_utils.py) ----
  useEffect(() => {
    const code = carteCadeauCodeInput.replace(/\D/g, "");
    if (code.length !== 12) { setCarteCadeauVerifyResult(null); return; }
    setCarteCadeauVerifying(true);
    const timeout = setTimeout(async () => {
      try {
        const { data } = await api.get(`/caisse/cartes-cadeaux/verify/${code}`);
        setCarteCadeauVerifyResult(data);
      } catch {
        setCarteCadeauVerifyResult({ valid: false, message: "Erreur de vérification" });
      } finally {
        setCarteCadeauVerifying(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [carteCadeauCodeInput]);

  const confirmCarteCadeauPayment = () => {
    const code = carteCadeauCodeInput.replace(/\D/g, "");
    if (code.length !== 12) { toast.error("Le code doit comporter 12 chiffres"); return; }
    if (!carteCadeauVerifyResult?.valid) { toast.error(carteCadeauVerifyResult?.message || "Carte cadeau invalide"); return; }
    // La carte ne peut jamais rendre de monnaie : si son solde est inférieur
    // au montant demandé, on applique seulement ce qu'elle permet réellement
    // — le reste devra être réglé par un autre moyen de paiement, comme pour
    // n'importe quel moyen non-espèces qui ne couvrirait pas tout le panier.
    const montantApplique = Math.min(carteCadeauPending.montant, carteCadeauVerifyResult.solde);
    if (montantApplique < carteCadeauPending.montant) {
      toast.error(`Solde insuffisant (${carteCadeauVerifyResult.solde.toFixed(2)} € disponible) — ${montantApplique.toFixed(2)} € appliqué(s), complétez avec un autre moyen de paiement`);
    }
    addPaymentEntry(carteCadeauPending.method, montantApplique, { carteCadeauCode: code });
  };

  const removePayment = (idx) => {
    setPayments((p) => {
      const next = p.filter((_, i) => i !== idx);
      const newTotalRegle = next.reduce((s, x) => s + (x.montant - x.rendu_monnaie), 0);
      const newReste = Math.max(0, Math.round((ttcEffective - newTotalRegle) * 100) / 100);
      setMontantInput(newReste > 0 ? newReste.toFixed(2) : "");
      return next;
    });
  };

  const validate = async (finalPayments, nonResidentOverride, forcePa = false) => {
    if (cart.length === 0) return;
    // Clé anti-doublon propre à cette tentative d'encaissement : un double clic ou un renvoi réseau
    // ne peut jamais enregistrer deux fois la même vente (voir create_ticket côté serveur).
    if (!idemKeyRef.current) idemKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    setValidating(true);
    setPaBlock(null);
    try {
      // On transmet le prix d'origine et le libellé de réduction éventuels tels quels
      // (plutôt que de les encoder dans le nom) pour que le ticket/la facture imprimée
      // puisse afficher le prix barré + le prix réduit.
      const items = cart.filter((i) => i.type !== "acompte_deduction" && i.type !== "reprise_deduction").map(({ type, article_id, nom, prix_unitaire, prix_origine, quantite, remise_label, tva_percent, prix_achat_ht, eco_participation_ht, menu_group_id, menu_nom }) => ({
        type, article_id, quantite, nom, prix_unitaire,
        prix_origine: remise_label ? prix_origine : null,
        remise_label: remise_label || null,
        tva_percent: tva_percent ?? 20,
        prix_achat_ht: prix_achat_ht || 0,
        eco_participation_ht: eco_participation_ht || 0,
        menu_group_id: menu_group_id || null,
        menu_nom: menu_nom || null,
      }));
      const paymentsPayload = (finalPayments ?? payments).map(({ mode, montant, rendu_monnaie, cheque_numero, cheque_nom, carte_cadeau_code }) => ({
        mode, montant, rendu_monnaie: rendu_monnaie || 0, cheque_numero, cheque_nom, carte_cadeau_code,
      }));
      const { data } = await api.post("/caisse", {
        // Vente forcée (PA injoignable) : ticket provisoire portant les infos client, à régulariser en facture.
        type: forcePa ? "ticket" : docType,
        idempotency_key: idemKeyRef.current,
        pa_provisoire: forcePa,
        shop_id: shopId,
        items,
        client_info: (docType === "facture" || forcePa) ? clientInfo : null,
        client_id: selectedClient?.id || null,
        payments: paymentsPayload,
        // Caisse physique sur laquelle cette vente est encaissée (mode
        // multi-caisses) — voir _theoretical_cash côté backend, qui l'utilise
        // pour calculer l'écart de CETTE caisse uniquement. `null` en mode
        // caisse unique ou si aucune session n'est ouverte (sans incidence :
        // les paiements espèces sont de toute façon bloqués dans ce cas).
        caisse_session_id: currentSession?.id || null,
        source_devis_id: sourceDevisId || null,
        source_reprise_id: cart.some((i) => i.type === "reprise_deduction") ? (sourceRepriseId || null) : null,
        acompte_devis_id: acompteDevisId || null,
        fidelite_points_used: selectedPalier ? selectedPalier.points : 0,
        // Plafond espèces relevé à 15 000 € (voir cashCeilingBlocked ci-dessous) :
        // n'est JAMAIS envoyé par défaut, seulement quand le vendeur a
        // explicitement confirmé le statut de non-résident après un premier
        // refus du serveur (plafond de 1 000 € dépassé).
        ...(nonResidentOverride ? {
          client_non_resident: true, client_id_reference: nonResidentOverride,
          identity_document_type: nonResidentIdType || null,
        } : {}),
      });
      idemKeyRef.current = null;
      toast.success(`${data.type === "facture" ? "Facture" : "Ticket"} ${data.numero} créé et sécurisé (N°${data.chain_seq})`);
      if (data.pa_provisoire) toast.warning("Ticket provisoire : à régulariser en facture dès le retour de la plateforme (bouton facture dans l'historique).");
      setCart([]);
      setSourceDevisId(null);
      setSourceRepriseId(null);
      setAcompteDevisId(null);
      setSelectedRdvInfo(null);
      // Un ou plusieurs rendez-vous étaient dans ce panier (cellules cliquées
      // dans le planning "Rendez-vous du jour") : la vente vient d'être
      // clôturée, ils passent en vert (statut "termine") partout où
      // l'agenda partagé s'affiche (Vente, Prise de rdv, tableau de bord).
      if (pendingRdvIds.length > 0) {
        const ids = pendingRdvIds;
        setPendingRdvIds([]);
        await Promise.all(ids.map((id) =>
          api.patch(`/rendez-vous/${id}/statut`, { statut: "termine", ticket_numero: data.numero }).catch(() => {})
        ));
      }
      if (shopSettings?.vente_mode === "rendez_vous") loadRdvDuJour();
      // Gestion par table : la table est encaissée, elle se libère —
      // retour à la grille des tables plutôt que de rester sur un panier
      // vidé sans contexte.
      if (activeTableId) {
        const tableIdToClose = activeTableId;
        setActiveTableId(null);
        api.delete(`/table-sessions/${tableIdToClose}`).catch(() => {});
        loadTables();
      }
      setSelectedClient(null);
      setClientSearch("");
      setSelectedPalierPoints("");
      setPayments([]);
      setPaymentDialogOpen(false);
      setCashCeilingBlocked(false);
      setNonResidentIdRef("");
      load();
      loadFiscal();
      // Loi AGEC (art. L541-15-10 code de l'environnement) : le ticket n'est
      // ouvert/imprimé automatiquement QUE si la boutique est explicitement
      // configurée en impression automatique (exemption légale, ex.
      // hôtellerie-restauration, ou choix assumé par l'exploitant). Par
      // défaut, on affiche seulement un bouton pour l'ouvrir sur demande du
      // client — voir le bandeau et le bouton "Ticket / Imprimer" ci-dessous.
      if (data.pa && !data.pa.pdf_ready) {
        // Facture suivie par la plateforme agréée : seul son PDF officiel s'imprime. On attend qu'il arrive.
        setLastCreatedDoc({ ...data, paReady: false });
        if (data.pa.status === "rejected") {
          toast.error(`Facture refusée par la plateforme : ${data.pa.message || "motif non précisé"}`);
        } else {
          toast.info("Facture transmise à la plateforme agréée : PDF officiel en cours de réception…");
          (async () => {
            for (let i = 0; i < 30; i++) {
              await new Promise((r) => setTimeout(r, 3000));
              try {
                const { data: s } = await api.get(`/pa/status/${data.id}`);
                if (s.pdf_ready) {
                  setLastCreatedDoc((d) => (d && d.id === data.id ? { ...d, paReady: true } : d));
                  if (shopSettings?.ticket_impression_auto) printTicketPdf(`${api.defaults.baseURL}/caisse/${data.id}/pdf`);
                  load();
                  return;
                }
                if (s.status === "rejected") { toast.error(`Facture refusée par la plateforme : ${s.message || ""}`); return; }
              } catch { /* on réessaie */ }
            }
            toast.warning("PDF officiel pas encore reçu : il sera accessible depuis l'historique dès sa réception.");
          })();
        }
      } else if (shopSettings?.ticket_impression_auto) {
        printTicketPdf(`${api.defaults.baseURL}/caisse/${data.id}/pdf`);
      } else {
        setLastCreatedDoc(data);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string" && detail.startsWith("PA_INDISPONIBLE")) {
        // Plateforme agréée injoignable : on propose de réessayer ou de forcer (ticket provisoire).
        setPaBlock({ finalPayments, nonResidentOverride, message: detail.replace(/^PA_INDISPONIBLE\s*:\s*/, "") });
        return;
      }
      // Plafond légal de paiement en espèces dépassé (1 000 €) : plutôt que de
      // simplement afficher l'erreur, on propose la voie légale de sortie
      // (client non-résident fiscal + pièce d'identité, plafond 15 000 €) au
      // lieu de forcer le vendeur à annuler des paiements déjà saisis.
      if (typeof detail === "string" && detail.includes("plafond légal") && detail.includes("espèces")) {
        setCashCeilingBlocked(true);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setValidating(false);
    }
  };

  const paymentMethodIcon = (key) => {
    if (key === "cb") return CreditCard;
    if (key === "espece") return Banknote;
    if (key === "cheque") return FileCheck2;
    if (key === "carte_cadeau") return Star;
    return Wallet;
  };


  // Conformité ISCA : un document validé est verrouillé (inaltérable). Les seules
  // actions possibles sont l'émission d'un avoir (correction tracée) ou une
  // annulation motivée — jamais une modification ou une suppression physique.
  // Une fois qu'un document a été inclus dans une clôture (ticket Z scellé), l'annulation
  // directe est bloquée côté backend (fausserait le cumul perpétuel déjà figé) : seul un
  // avoir reste possible. On adapte donc les boutons affichés en conséquence.
  // Un duplicata (copie d'un ticket/facture à la demande du client) n'est pas une
  // vente distincte : ni avoir, ni annulation ne doivent porter dessus (il faut
  // agir sur le document source). On masque donc ces actions pour ce cas.
  const canDoFiscalActions = (t) => canManageFiscal && !t.annule && t.type !== "avoir" && (!t.avoir_id || (t.avoirs_partiels && !t.avoir_solde)) && !t.duplicata;
  const canAnnuler = (t) => canDoFiscalActions(t) && !t.cloture_id && !t.avoir_id;

  // La facturation est ouverte à tout utilisateur pouvant gérer ce ticket (vendeur
  // d'origine, ou droits fiscaux étendus) — ce n'est pas une correction sensible,
  // juste une mise en forme légale à la demande du client.
  const canFacturer = (t) => t.type === "ticket" && !t.annule
    && (user?.is_admin || t.vendeur_id === user?.id || canManageFiscal);

  const openFacturer = (t) => {
    const ci = t.client_info || {};
    setFacturerTicket(t);
    setFacturerInfo({ nom: ci.nom || "", adresse: ci.adresse || "", email: ci.email || "", siret: ci.siret || "", siren: ci.siren || "", tva_intracom: ci.tva_intracom || "", adresse_livraison: ci.adresse_livraison || "" });
  };

  const confirmFacturer = async () => {
    if (!facturerInfo.nom.trim()) { toast.error("Le nom du client est obligatoire"); return; }
    try {
      const { data } = await api.post(`/caisse/${facturerTicket.id}/facturer`, { client_info: facturerInfo });
      toast.success(`Duplicata facture ${data.numero} émis pour le ticket ${facturerTicket.numero}`);
      setFacturerTicket(null);
      load();
      if (data.pa && !data.pa.pdf_ready) {
        toast.info(data.pa.status === "rejected" ? `Refusée par la plateforme : ${data.pa.message || ""}` : "Facture transmise à la plateforme agréée : le PDF officiel sera disponible dans la liste dès réception.");
      } else {
        window.open(`${api.defaults.baseURL}/caisse/${data.id}/pdf`, "_blank");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openMailDialog = (t) => {
    const label = t.type === "facture" ? "facture" : "ticket";
    setMailTicket(t);
    setMailTo(t.client_info?.email || "");
    setMailSubject(`Votre ${label} ${t.numero || ""}`);
    setMailMessage(`Bonjour ${t.client_info?.nom || ""},\n\nVeuillez trouver ci-joint votre ${label} ${t.numero || ""}.\n\nCordialement,`);
  };

  const sendTicketMail = async () => {
    if (!mailTo) { toast.error("Saisissez l'adresse e-mail du client"); return; }
    setMailSending(true);
    try {
      const res = await api.post(`/caisse/${mailTicket.id}/send-email`, { to_email: mailTo, subject: mailSubject, message: mailMessage });
      toast.success(`Document envoyé à ${res.data.sent_to}`);
      setMailTicket(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setMailSending(false);
    }
  };


  // Montant TTC d'un avoir partiel : lignes sélectionnées + réductions (fidélité,
  // acompte) remboursées au prorata — même règle que le serveur, qui fait foi.
  const avoirLignesPayload = () => Object.entries(avoirLignes)
    .map(([index, quantite]) => ({ index: Number(index), quantite: Number(quantite) }))
    .filter((l) => l.quantite > 0);
  const avoirMontantPartiel = () => {
    const items = avoirTicket?.items || [];
    const ttcOf = (i, q) => i.prix_unitaire * q * (1 + (Number(i.tva_percent ?? 20) || 0) / 100);
    const posTotal = items.filter((i) => i.prix_unitaire > 0).reduce((a, i) => a + ttcOf(i, i.quantite), 0);
    const sel = avoirLignesPayload().reduce((a, l) => a + (items[l.index] ? ttcOf(items[l.index], l.quantite) : 0), 0);
    const ratio = posTotal > 0 ? sel / posTotal : 0;
    const remises = items.filter((i) => i.prix_unitaire < 0).reduce((a, i) => a + ttcOf(i, i.quantite) * ratio, 0);
    return Math.round((sel + remises) * 100) / 100;
  };

  const openAvoir = (t) => {
    setAvoirPartiel(false);
    setAvoirLignes({});
    setAvoirTicket(t);
    setAvoirMode("");
    setAvoirChequeNumero("");
    setAvoirChequeNom("");
    setAvoirMotif("");
    setAvoirEmettreCarteCadeau(false);
    setAvoirCarteCadeauValidite("");
  };

  const confirmAvoir = async () => {
    if (avoirEmettreCarteCadeau) {
      if (avoirPartiel && avoirLignesPayload().length === 0) { toast.error("Sélectionnez au moins une ligne à rembourser"); return; }
      setAvoirSaving(true);
      try {
        const { data } = await api.post(`/caisse/${avoirTicket.id}/avoir`, {
          motif: avoirMotif.trim(),
          ...(avoirPartiel ? { lignes: avoirLignesPayload() } : {}),
          emettre_carte_cadeau: true,
          carte_cadeau_validite_jours: avoirCarteCadeauValidite ? Number(avoirCarteCadeauValidite) : null,
        });
        toast.success(`Avoir ${data.numero} émis — carte cadeau ${data.carte_cadeau_emise?.code} générée`);
        setAvoirTicket(null);
        load();
        loadCartesCadeaux();
        if (data.carte_cadeau_emise) window.open(`${api.defaults.baseURL}/caisse/cartes-cadeaux/${data.carte_cadeau_emise.id}/pdf`, "_blank");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setAvoirSaving(false);
      }
      return;
    }
    if (avoirPartiel && avoirLignesPayload().length === 0) { toast.error("Sélectionnez au moins une ligne à rembourser"); return; }
    if (!avoirMode) { toast.error("Choisissez le moyen par lequel le client a été remboursé"); return; }
    const method = paymentMethods.find((m) => m.key === avoirMode);
    if (method?.requires_cheque_info && (!avoirChequeNumero.trim() || !avoirChequeNom.trim())) {
      toast.error("Le n° de chèque et le nom du titulaire sont obligatoires");
      return;
    }
    setAvoirSaving(true);
    try {
      const payments = [{
        mode: avoirMode,
        montant: avoirPartiel ? avoirMontantPartiel() : avoirTicket.total_ttc,
        cheque_numero: method?.requires_cheque_info ? avoirChequeNumero : null,
        cheque_nom: method?.requires_cheque_info ? avoirChequeNom : null,
      }];
      const { data } = await api.post(`/caisse/${avoirTicket.id}/avoir`, {
        payments, motif: avoirMotif.trim(),
        ...(avoirPartiel ? { lignes: avoirLignesPayload() } : {}),
        caisse_session_id: currentSession?.id || null,
      });
      toast.success(`Avoir ${data.numero} émis`);
      setAvoirTicket(null);
      load();
      printTicketPdf(`${api.defaults.baseURL}/caisse/${data.id}/pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAvoirSaving(false);
    }
  };

  const openAnnuler = (t) => { setAnnulerTicket(t); setAnnulerMotif(""); };

  const confirmAnnuler = async () => {
    if (!annulerMotif.trim()) { toast.error("Le motif d'annulation est obligatoire"); return; }
    try {
      await api.post(`/caisse/${annulerTicket.id}/annuler`, { motif: annulerMotif.trim() });
      toast.success("Document annulé (tracé, non supprimé)");
      setAnnulerTicket(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Clôture ANTÉRIEURE / de régularisation uniquement : la clôture manuelle
  // d'une période en cours a été retirée (bouton + fonction `doCloture`
  // supprimés) — elle empêchait la clôture automatique programmée de
  // s'exécuter ensuite pour cette même période, ce qui n'est jamais
  // souhaitable. Seule la clôture automatique doit clore une période qui
  // vient de se terminer ; ceci reste réservé à une période RÉELLEMENT déjà
  // terminée (le serveur refuse désormais toute tentative sur une période
  // encore en cours, voir fiscal_utils.perform_cloture). Ne modifie jamais
  // une clôture déjà émise : si des documents de cette même période restent
  // non clôturés, le backend émet automatiquement un "complément" chaîné et
  // référencé à la clôture d'origine.
  const [pastPeriode, setPastPeriode] = useState("jour");
  const [pastDate, setPastDate] = useState("");

  const doClotureAnterieure = async () => {
    if (!pastDate) { toast.error("Choisissez une date"); return; }
    try {
      const { data } = await api.post(`/caisse/cloture/${pastPeriode}`, null, { params: { date: pastDate } });
      const suffix = data.complementaire ? ` — complément n°${data.numero_complement} d'une clôture déjà existante` : "";
      toast.success(`Clôture antérieure effectuée (${data.nb_documents} document(s))${suffix}`);
      setPastDate("");
      loadFiscal();
      load();
      window.open(`${api.defaults.baseURL}/caisse/cloture/${data.id}/export`, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const persistComptaEmails = async (nextEmails) => {
    if (!shopId) { toast.error("Veuillez sélectionner une boutique de travail"); return false; }
    setComptaEmailSaving(true);
    try {
      const { data } = await api.patch("/caisse/fiscal-settings", {
        compta_emails: nextEmails,
        fiscal_anchor_email_secondaire: anchorEmailSecondaire.trim(),
        ticket_prix_affichage: ticketPrixAffichage,
      });
      setComptaEmails(data.compta_emails || []);
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    } finally {
      setComptaEmailSaving(false);
    }
  };

  const addComptaEmail = async () => {
    const email = newComptaEmail.trim();
    if (!email) return;
    if (comptaEmails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      toast.error("Cette adresse est déjà dans la liste");
      return;
    }
    if (await persistComptaEmails([...comptaEmails, email])) {
      setNewComptaEmail("");
      toast.success("Destinataire ajouté");
    }
  };

  const startEditComptaEmail = (index) => {
    setEditingComptaEmailIndex(index);
    setEditingComptaEmailValue(comptaEmails[index]);
  };

  const saveEditComptaEmail = async () => {
    const value = editingComptaEmailValue.trim();
    if (!value) { toast.error("L'adresse ne peut pas être vide"); return; }
    const next = comptaEmails.map((e, i) => (i === editingComptaEmailIndex ? value : e));
    if (await persistComptaEmails(next)) {
      setEditingComptaEmailIndex(null);
      toast.success("Destinataire modifié");
    }
  };

  const removeComptaEmail = async (index) => {
    const next = comptaEmails.filter((_, i) => i !== index);
    if (await persistComptaEmails(next)) toast.success("Destinataire supprimé");
  };

  const saveAnchorEmailSecondaire = async () => {
    if (!shopId) { toast.error("Veuillez sélectionner une boutique de travail"); return; }
    setComptaEmailSaving(true);
    try {
      await api.patch("/caisse/fiscal-settings", {
        compta_emails: comptaEmails,
        fiscal_anchor_email_secondaire: anchorEmailSecondaire.trim(),
        ticket_prix_affichage: ticketPrixAffichage,
      });
      toast.success("Second destinataire enregistré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setComptaEmailSaving(false);
    }
  };

  const saveTicketPrixAffichage = async (value) => {
    if (!shopId) { toast.error("Veuillez sélectionner une boutique de travail"); return; }
    setTicketPrixAffichageSaving(true);
    try {
      const { data } = await api.patch("/caisse/fiscal-settings", {
        compta_emails: comptaEmails,
        fiscal_anchor_email_secondaire: anchorEmailSecondaire.trim(),
        ticket_prix_affichage: value,
      });
      setTicketPrixAffichage(data.ticket_prix_affichage);
      toast.success("Affichage des prix sur le ticket mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTicketPrixAffichageSaving(false);
    }
  };

  const exportArchive = async () => {
    try {
      const { data } = await api.get("/caisse/archive/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archive-fiscale-${shopId}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Archive fiscale exportée");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Correctif demandé : ce bouton n'affiche plus que la DERNIÈRE attestation
  // déjà émise — il n'en génère plus jamais une automatiquement. S'il n'en
  // existe encore aucune, on l'indique clairement plutôt que de créer un
  // document silencieusement (voir GET /caisse/attestation, désormais 404 en
  // l'absence d'attestation). Créer une attestation reste un acte volontaire
  // depuis Administrateurs > Conformité fiscale.
  const viewAttestation = async () => {
    try {
      const res = await api.get("/caisse/attestation-signee", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) {
      let message = "Aucune attestation signée disponible";
      try {
        const text = await e.response?.data?.text?.();
        if (text) message = formatApiError(JSON.parse(text)?.detail) || message;
      } catch { /* réponse non-JSON, on garde le message par défaut */ }
      toast.error(message);
    }
  };

  return (
    <Layout title="Caisse">
      {hasPerm(user, "caisse", "access") && (
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 mb-4 ${
            currentSession ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"
          }`}
          data-testid="cash-session-banner"
        >
          {currentSession === undefined ? (
            <p className="text-sm text-muted-foreground">Chargement de la session de caisse...</p>
          ) : currentSession ? (
            <>
              <div className="text-sm">
                <span className="font-medium">
                  {shopSettings?.caisse_mode === "multiple" ? `Caisse ${currentSession.caisse_numero} ouverte` : "Session de caisse ouverte"}
                </span>
                <span className="text-muted-foreground"> — fond de départ {currentSession.fond_ouverture.toFixed(2)} € par {currentSession.ouverte_par_nom}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {hasPerm(user, "caisse", "ouvrir_fermer_fond") && (
                  <>
                    <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => openMouvementDialog("entree")} data-testid="cash-session-entree-button">
                      <ArrowDownToLine size={14} /> Entrée
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1.5" onClick={() => openMouvementDialog("sortie")} data-testid="cash-session-sortie-button">
                      <ArrowUpFromLine size={14} /> Sortie
                    </Button>
                  </>
                )}
                {shopSettings?.caisse_mode === "multiple" && (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={leaveSession} data-testid="cash-session-leave-button">
                    <LogOut size={14} /> Quitter cette caisse
                  </Button>
                )}
                {hasPerm(user, "caisse", "ouvrir_fermer_fond") && (
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => { setClosePreview(null); setFondFermetureReel(""); setSessionNotes(""); setSessionCloseDialog(true); }} data-testid="cash-session-close-button">
                    <Lock size={14} /> Fermer la caisse (comptage)
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-muted-foreground">Aucune session de caisse ouverte — les paiements et remboursements en espèces sont bloqués tant que la caisse n'est pas ouverte (carte et chèque restent utilisables).</div>
              {hasPerm(user, "caisse", "ouvrir_fermer_fond") && (
                <Button size="sm" variant="outline" className="gap-2" onClick={openSessionFlow} data-testid="cash-session-open-button">
                  <Wallet size={14} /> Ouvrir la caisse (fond de départ)
                </Button>
              )}
            </>
          )}
        </div>
      )}
      <Tabs defaultValue={defaultTab} className="mb-4">
        <TabsList>
          {hasPerm(user, "caisse", "voir_vente") && <TabsTrigger value="vente" data-testid="tab-vente">Vente</TabsTrigger>}
          {hasPerm(user, "caisse", "voir_vente") && shopSettings?.vente_mode === "rendez_vous" && (
            <TabsTrigger value="prise-rdv" data-testid="tab-prise-rdv" className="gap-1.5">
              <CalendarDays size={14} /> Prise de rdv
            </TabsTrigger>
          )}
          {hasPerm(user, "caisse", "voir_carte_cadeau") && <TabsTrigger value="carte-cadeau" data-testid="tab-carte-cadeau">Carte cadeau</TabsTrigger>}
          {canFiscal && <TabsTrigger value="fiscal" data-testid="tab-fiscal">Registre fiscal</TabsTrigger>}
        </TabsList>

        <TabsContent value="vente">
          {ventesLock?.locked ? (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-8 text-center max-w-lg mx-auto" data-testid="ventes-locked-banner">
              <Lock size={32} className="mx-auto mb-3 text-amber-600" />
              <p className="font-heading font-semibold text-lg mb-1">Onglet Ventes verrouillé</p>
              <p className="text-sm text-muted-foreground mb-4">
                La vente est bloquée depuis {ventesLock.lock_hour} jusqu'à 00h01 demain matin.
                Un administrateur peut forcer la réouverture immédiatement.
              </p>
              <Button variant="outline" className="gap-2" onClick={() => setReopenDialogOpen(true)} data-testid="ventes-force-reopen-button">
                <ShieldCheck size={16} />Forcer la réouverture (code administrateur)
              </Button>
            </div>
          ) : shopSettings?.vente_mode === "par_table" && !activeTableId ? (
            <div className="max-w-2xl mx-auto" data-testid="tables-grid">
              <div className="flex gap-2 mb-6">
                <Input
                  placeholder="Numéro ou nom de table (ex. 5, Terrasse 2)" value={newTableNumero}
                  onChange={(e) => setNewTableNumero(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && ouvrirTable()}
                  data-testid="nouvelle-table-input"
                />
                <Button onClick={ouvrirTable} className="gap-2 shrink-0" data-testid="nouvelle-table-ouvrir">
                  <Plus size={16} />Ouvrir une table
                </Button>
              </div>
              {tables.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">Aucune table ouverte pour le moment.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="tables-list">
                  {tables.map((t) => {
                    const total = (t.lignes || []).reduce((s, l) => s + l.prix_unitaire * (1 + (l.tva_percent || 0) / 100) * l.quantite, 0);
                    return (
                      <div key={t.id} className="relative" data-testid={`table-card-${t.id}`}>
                        <button
                          type="button"
                          onClick={() => reprendreTable(t)}
                          className="w-full flex flex-col items-center justify-center gap-1 rounded-xl border border-border bg-card p-4 text-center hover:border-primary transition-colors min-h-[92px]"
                        >
                          <span className="text-sm font-semibold">Table {t.numero_table}</span>
                          <span className="text-xs text-muted-foreground">{(t.lignes || []).length} article(s)</span>
                          {total > 0 && <span className="text-sm font-medium text-primary">{total.toFixed(2)} €</span>}
                        </button>
                        <button
                          type="button"
                          onClick={() => fermerTableSansEncaisser(t.id)}
                          className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5"
                          title="Fermer sans encaisser"
                          data-testid={`table-fermer-${t.id}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {shopSettings?.vente_mode === "par_table" && activeTable && (
              <div className="lg:col-span-3 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-2" data-testid="table-active-banner">
                <span className="text-sm font-medium">Table {activeTable.numero_table}</span>
                <Button variant="outline" size="sm" onClick={changerDeTable} data-testid="changer-de-table">Changer de table</Button>
              </div>
            )}
            <div className="lg:col-span-2 lg:order-1">
              {shopSettings?.vente_mode === "rendez_vous" && (
                <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="vente-rdv-schedule">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-heading font-semibold flex items-center gap-1.5"><CalendarDays size={15} className="text-primary" />Rendez-vous du jour</p>
                    <span className="text-xs text-muted-foreground">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
                  </div>
                  {rdvCreneaux.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="vente-rdv-schedule-empty">Aucun rendez-vous aujourd'hui.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-separate border-spacing-1 text-xs">
                        <thead>
                          <tr>
                            <th className="w-12"></th>
                            {rdvColonnesVendeurs.map((v) => (
                              <th key={v.id} className="text-center font-medium text-muted-foreground pb-1 min-w-[110px]" data-testid={`vente-rdv-col-${v.id}`}>
                                {v.prenom} {v.nom}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rdvCreneaux.map((slot) => (
                            <tr key={slot.toISOString()}>
                              <td className="text-muted-foreground text-right pr-2 align-middle whitespace-nowrap">
                                {slot.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                              </td>
                              {rdvColonnesVendeurs.map((v) => {
                                const rdv = rdvAuCreneau(v.id, slot);
                                if (!rdv) return <td key={v.id} className="h-10 rounded-md bg-secondary/30" />;
                                const c = couleurRdv(rdv);
                                return (
                                  <td key={v.id} className="p-0">
                                    <button
                                      type="button"
                                      onClick={() => addRdvToCart(rdv)}
                                      title={`${rdv.client_nom || rdv.titre} — cliquer pour ajouter au panier`}
                                      data-testid={`vente-rdv-cell-${rdv.id}`}
                                      className={`w-full h-10 rounded-md px-1.5 text-left leading-tight hover:ring-2 transition-shadow ${c.bg} ${c.text} ${c.ring}`}
                                    >
                                      <span className="block truncate font-medium">
                                        {rdv.client_nom ? rdv.client_nom : (rdv.prestations?.[0]?.nom || rdv.titre)}
                                      </span>
                                      {rdv.prestations?.length > 0 && (
                                        <span className="block truncate text-[10px] opacity-80">{rdv.prestations.map((p) => p.nom).join(", ")}</span>
                                      )}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {shopSettings?.vente_mode === "rendez_vous" ? (
                <div className="mb-6" data-testid="vente-rdv-categories-container">
                  <div className="flex gap-2 mb-3" data-testid="vente-rdv-categories">
                    <button
                      type="button"
                      onClick={() => { setVenteRdvCategorie("prestation"); setVenteRdvArticleSearch(""); }}
                      data-testid="vente-rdv-categorie-prestation"
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${venteRdvCategorie === "prestation" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                    >
                      <Scissors size={15} />Prestation
                    </button>
                    <button
                      type="button"
                      onClick={() => { setVenteRdvCategorie("produit"); setVenteRdvArticleSearch(""); }}
                      data-testid="vente-rdv-categorie-produit"
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${venteRdvCategorie === "produit" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                    >
                      <Package size={15} />Produit
                    </button>
                  </div>
                  <div className="relative mb-3">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      data-testid="vente-rdv-article-search"
                      placeholder={venteRdvCategorie === "prestation" ? "Rechercher une prestation..." : "Rechercher un produit..."}
                      value={venteRdvArticleSearch}
                      onChange={(e) => setVenteRdvArticleSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="vente-rdv-articles-grid">
                    {venteRdvArticlesFiltres.map((a) => {
                      const outOfStock = a.type !== "formule" && a.type !== "service" && !a.stock_infini && (a.quantite || 0) <= 0;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleArticleClick(a)}
                          data-testid={`vente-rdv-article-${a.id}`}
                          className={`flex flex-col items-center justify-center gap-1 rounded-xl border border-border bg-card p-4 text-center hover:border-primary transition-colors min-h-[84px] ${outOfStock ? "opacity-60" : ""}`}
                        >
                          {a.type === "formule" && <span className="text-[9px] font-semibold text-amber-600 uppercase tracking-wide">Formule</span>}
                          <span className="text-sm font-medium leading-tight">{a.nom}</span>
                          <span className="text-sm font-semibold text-primary">{(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €{a.vendu_au_poids ? `/${uniteDe(a)}` : ""}</span>
                        </button>
                      );
                    })}
                    {venteRdvArticlesFiltres.length === 0 && (
                      <p className="text-sm text-muted-foreground col-span-full text-center py-4" data-testid="vente-rdv-articles-empty">
                        {venteRdvArticleSearch.trim()
                          ? "Aucun résultat pour cette recherche."
                          : venteRdvCategorie === "prestation" ? "Aucune prestation (article de type Service) en stock." : "Aucun produit en stock."}
                      </p>
                    )}
                  </div>
                </div>
              ) : shopSettings?.vente_mode === "grille_tactile" ? (
                <div className="mb-6" data-testid="grille-tactile-container">
                  <div className="flex gap-2 overflow-x-auto pb-2 mb-3" data-testid="grille-tactile-categories">
                    {categoriesAvecArticles.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setGrilleCategorie(c.nom)}
                        data-testid={`grille-tactile-categorie-${c.id}`}
                        className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg shrink-0 min-w-[64px] transition-colors ${grilleCategorieActive === c.nom ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                      >
                        <CategoryIcon category={c} uploadsBase={UPLOADS_BASE} size={20} />
                        <span className="text-[11px] font-medium">{c.nom}</span>
                      </button>
                    ))}
                    {categoriesAvecArticles.length === 0 && (
                      <p className="text-sm text-muted-foreground py-2">Aucun article en stock — ajoutez des articles avec une catégorie depuis l'onglet Stock.</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="grille-tactile-articles">
                    {articlesDeLaCategorie.map((a) => {
                      const outOfStock = a.type !== "formule" && a.type !== "service" && !a.stock_infini && (a.quantite || 0) <= 0;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => handleArticleClick(a)}
                          data-testid={`grille-tactile-article-${a.id}`}
                          className={`flex flex-col items-center justify-center gap-1 rounded-xl border border-border bg-card p-4 text-center hover:border-primary transition-colors min-h-[84px] ${outOfStock ? "opacity-60" : ""}`}
                        >
                          {a.type === "formule" && <span className="text-[9px] font-semibold text-amber-600 uppercase tracking-wide">Formule</span>}{a.type === "service" && <span className="text-[9px] font-semibold text-sky-600 uppercase tracking-wide">Service</span>}
                          <span className="text-sm font-medium leading-tight">{a.nom}</span>
                          <span className="text-sm font-semibold text-primary">{(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €{a.vendu_au_poids ? `/${uniteDe(a)}` : ""}</span>
                        </button>
                      );
                    })}
                    {grilleCategorieActive && articlesDeLaCategorie.length === 0 && (
                      <p className="text-sm text-muted-foreground col-span-full text-center py-4">Aucun article dans cette catégorie.</p>
                    )}
                  </div>
                </div>
              ) : (
              <>
              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="caisse-article-search" placeholder="Rechercher un article..." value={articleSearch} onChange={(e) => setArticleSearch(e.target.value)} className="pl-9" />
              </div>
              {outOfStockCount > 0 && (
                <button
                  type="button"
                  data-testid="caisse-toggle-out-of-stock"
                  onClick={() => setShowOutOfStock((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2 -mt-1"
                >
                  <PackageX size={13} />
                  {showOutOfStock
                    ? "Masquer les articles en rupture"
                    : `Afficher aussi les articles en rupture (${outOfStockCount})`}
                </button>
              )}
              <div className="space-y-1.5 mb-6 max-h-80 overflow-y-auto" data-testid="caisse-article-list">
                {filteredArticles.map((a) => {
                  const outOfStock = a.type !== "formule" && a.type !== "service" && !a.stock_infini && (a.quantite || 0) <= 0;
                  return (
                  <button
                    key={a.id}
                    data-testid={`caisse-article-${a.id}`}
                    onClick={() => handleArticleClick(a)}
                    className={`w-full flex items-center gap-3 rounded-lg border border-border bg-card p-2 hover:border-primary transition-colors duration-200 text-left ${outOfStock ? "opacity-60" : ""}`}
                  >
                    <div className="w-10 h-10 rounded-md bg-secondary overflow-hidden flex items-center justify-center shrink-0">
                      {a.photo_url ? (
                        <img src={`${UPLOADS_BASE}${a.photo_url}`} className="w-full h-full object-cover" />
                      ) : (
                        <CategoryIcon category={categories.find((c) => c.nom === a.categorie)} uploadsBase={UPLOADS_BASE} size={18} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate flex items-center gap-1.5">
                        {a.nom}
                        {a.type === "formule" && <span className="text-[9px] font-semibold text-amber-600 uppercase tracking-wide shrink-0">Formule</span>}
                        {a.type === "service" && <span className="text-[9px] font-semibold text-sky-600 uppercase tracking-wide shrink-0">Service</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.categorie} · Qté: <span className={outOfStock ? "text-destructive font-medium" : ""}>{a.quantite}</span> · TVA {a.tva_percent ?? 20}%
                      </p>
                      <p
                        className={`text-[10px] font-medium mt-0.5 ${(a.stock_type || "principal") === "principal" ? "text-primary" : "text-amber-600 dark:text-amber-400"}`}
                        title="Information interne pour la caisse — n'apparaît jamais sur le ticket imprimé"
                      >
                        {(a.stock_type || "principal") === "principal" ? "Stock principal" : "Autre fournisseur"}
                      </p>
                    </div>
                    <p className="text-sm font-semibold shrink-0">{(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €{a.vendu_au_poids ? `/${uniteDe(a)}` : ""}</p>
                  </button>
                  );
                })}
                {filteredArticles.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="caisse-article-list-empty">
                    {showOutOfStock ? "Aucun article ne correspond à cette recherche." : "Aucun article en stock ne correspond à cette recherche."}
                  </p>
                )}
              </div>
              </>
              )}

              <div className="rounded-xl border border-border bg-card p-4">
                <p className="font-heading font-semibold mb-3">Prestation libre</p>
                <div className="flex gap-2">
                  <Input data-testid="prestation-name-input" placeholder="Nom de la prestation" value={prestationName} onChange={(e) => setPrestationName(e.target.value)} className="flex-1" />
                  <Input data-testid="prestation-price-input" type="number" min="0" step="0.01" placeholder="Prix TTC €" value={prestationPrice} onChange={(e) => setPrestationPrice(e.target.value)} className="w-28" />
                  <Input data-testid="prestation-tva-input" type="number" min="0" max="100" step="0.1" placeholder="TVA %" value={prestationTva} onChange={(e) => setPrestationTva(e.target.value)} className="w-20" title="Taux de TVA" />
                  <Button data-testid="prestation-add-button" onClick={addPrestation}><Plus size={16} /></Button>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                  <p className="font-heading font-semibold">Tickets du jour</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative w-full sm:w-56">
                      <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input data-testid="caisse-history-search" placeholder="Rechercher..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} className="pl-8 h-8 text-sm" />
                    </div>
                    <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => setAnteriorDialogOpen(true)} data-testid="caisse-anterior-tickets-button">
                      <Clock size={13} />Tickets antérieurs
                    </Button>
                  </div>
                </div>
                {filteredHistory.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="caisse-history-empty">Aucun ticket aujourd'hui pour l'instant.</p>
                )}
                <div className="space-y-1" data-testid="caisse-history-list">
                  {filteredHistory.map((t) => renderTicketRow(t, "history"))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Conformément à la réglementation anti-fraude TVA (ISCA), un document validé ne peut plus être
                  modifié ni supprimé : utilisez un <strong>avoir</strong> pour corriger, une <strong>annulation motivée</strong>
                  {" "}pour l'invalider, ou <strong>Facturer</strong> pour obtenir un duplicata facture d'un ticket — le ticket
                  original n'est jamais modifié et le duplicata n'est pas recompté dans le chiffre d'affaires.
                </p>
              </div>
            </div>

            <Dialog open={anteriorDialogOpen} onOpenChange={setAnteriorDialogOpen}>
              <DialogContent className="sm:max-w-2xl" data-testid="caisse-anterior-dialog">
                <DialogHeader><DialogTitle>Tickets antérieurs</DialogTitle></DialogHeader>
                <div className="flex flex-col sm:flex-row gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher (numéro, vendeur, client)..."
                      value={anteriorSearch}
                      onChange={(e) => setAnteriorSearch(e.target.value)}
                      className="pl-8"
                      data-testid="caisse-anterior-search"
                    />
                  </div>
                  <Input
                    type="date"
                    value={anteriorDate}
                    onChange={(e) => setAnteriorDate(e.target.value)}
                    className="sm:w-44"
                    data-testid="caisse-anterior-date"
                  />
                  {anteriorDate && (
                    <Button variant="ghost" size="sm" onClick={() => setAnteriorDate("")}>Effacer la date</Button>
                  )}
                </div>
                <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="caisse-anterior-list">
                  {filteredAnteriorTickets.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="caisse-anterior-empty">Aucun ticket ne correspond à ces filtres.</p>
                  )}
                  {filteredAnteriorTickets.map((t) => renderTicketRow(t, "anterior"))}
                </div>
              </DialogContent>
            </Dialog>

            <div className="rounded-xl border border-border bg-card p-5 h-fit lg:sticky lg:top-24 lg:order-2">
              {hasPerm(user, "client", "view") && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-1.5 flex items-center gap-1.5"><Contact size={13} /> Client (optionnel)</p>
                {selectedClient ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm" data-testid="caisse-selected-client">
                    <span className="truncate">{selectedClient.prenom} {selectedClient.nom}</span>
                    <button onClick={() => { setSelectedClient(null); setClientSearch(""); }} data-testid="caisse-client-clear"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      data-testid="caisse-client-search"
                      placeholder="Rechercher un client..."
                      value={clientSearch}
                      onChange={(e) => { setClientSearch(e.target.value); setClientDropdownOpen(true); }}
                      onFocus={() => setClientDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setClientDropdownOpen(false), 150)}
                    />
                    {clientDropdownOpen && clientResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-md" data-testid="caisse-client-results">
                        {clientResults.map((c) => (
                          <button
                            key={c.id}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                            data-testid={`caisse-client-option-${c.id}`}
                            onMouseDown={() => { setSelectedClient(c); setClientSearch(""); setClientResults([]); }}
                          >
                            {c.prenom} {c.nom} {c.telephone ? `· ${c.telephone}` : ""}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {selectedClient && fidelite?.active && clientFideliteBalance > 0 && (
                <div className="mb-4 rounded-lg border border-border bg-secondary/30 p-2.5" data-testid="caisse-fidelite-panel">
                  <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                    <Star size={12} />Solde fidélité : <span className="font-semibold text-foreground">{clientFideliteBalance} points</span>
                  </p>
                  {(() => {
                    const eligiblePaliers = (fidelite.paliers_cartes_cadeaux || [])
                      .filter((p) => clientFideliteBalance >= p.points)
                      .sort((a, b) => a.points - b.points);
                    if (eligiblePaliers.length === 0) {
                      return <p className="text-xs text-muted-foreground">Aucun palier accessible avec ce solde (voir Administration &gt; Fidélité).</p>;
                    }
                    return (
                      <Select value={selectedPalierPoints} onValueChange={setSelectedPalierPoints}>
                        <SelectTrigger className="h-8 text-sm" data-testid="caisse-fidelite-palier-select">
                          <SelectValue placeholder="Utiliser des points..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Ne pas utiliser de points</SelectItem>
                          {eligiblePaliers.map((p) => (
                            <SelectItem key={p.points} value={String(p.points)}>
                              {p.points} points → -{p.montant.toFixed(2)} €
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                  {fideliteDiscountAmount > 0 && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">
                      Réduction appliquée : -{fideliteDiscountAmount.toFixed(2)} €
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-2 mb-4">
                <Button variant={docType === "ticket" ? "default" : "outline"} size="sm" data-testid="doctype-ticket" onClick={() => setDocType("ticket")}>Ticket</Button>
                <Button variant={docType === "facture" ? "default" : "outline"} size="sm" data-testid="doctype-facture" onClick={() => setDocType("facture")}>Facture</Button>
              </div>

              {docType === "facture" && (
                <div className="space-y-2 mb-4">
                  <Input data-testid="facture-client-nom" placeholder="Nom du client" value={clientInfo.nom} onChange={(e) => setClientInfo({ ...clientInfo, nom: e.target.value })} />
                  <Input data-testid="facture-client-adresse" placeholder="Adresse" value={clientInfo.adresse} onChange={(e) => setClientInfo({ ...clientInfo, adresse: e.target.value })} />
                  <Input data-testid="facture-client-email" placeholder="Email" value={clientInfo.email} onChange={(e) => setClientInfo({ ...clientInfo, email: e.target.value })} />
                  <Input data-testid="facture-client-siret" placeholder="N° SIRET client (optionnel)" value={clientInfo.siret} onChange={(e) => setClientInfo({ ...clientInfo, siret: e.target.value })} />
                  <Input data-testid="facture-client-siren" placeholder="N° SIREN client (obligatoire si professionnel assujetti à la TVA)" value={clientInfo.siren} onChange={(e) => setClientInfo({ ...clientInfo, siren: e.target.value })} />
                  <Input data-testid="facture-client-tva-intracom" placeholder="N° TVA intracommunautaire client (optionnel, UE)" value={clientInfo.tva_intracom} onChange={(e) => setClientInfo({ ...clientInfo, tva_intracom: e.target.value })} />
                  <Input data-testid="facture-client-adresse-livraison" placeholder="Adresse de livraison (si différente, optionnel)" value={clientInfo.adresse_livraison} onChange={(e) => setClientInfo({ ...clientInfo, adresse_livraison: e.target.value })} />
                </div>
              )}

              {selectedRdvInfo && cart.length > 0 && (
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5" data-testid="cart-rdv-badge">
                  <CalendarDays size={12} />RDV {selectedRdvInfo.heure} · {selectedRdvInfo.label}
                </p>
              )}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto" data-testid="cart-list">
                {cart.length === 0 && <p className="text-sm text-muted-foreground">Panier vide</p>}
                {cartGroupedByTva.map((group) => (
                  <div key={group.tva_percent}>
                    {/* Le sous-titre TVA n'apparaît que si le panier mélange plusieurs
                        taux — sinon inutile d'alourdir l'affichage pour un seul taux. */}
                    {cartGroupedByTva.length > 1 && (
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1" data-testid={`cart-group-tva-${group.tva_percent}`}>
                        TVA {group.tva_percent}%
                      </p>
                    )}
                    <div className="space-y-2">
                      {group.entries.map(({ item, idx }) => (
                        <div key={idx} className="flex items-center justify-between text-sm border-b border-border pb-2 gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="truncate">{item.nom}</p>
                            {item.remise_label ? (
                              <p className="text-xs text-muted-foreground">
                                <span className="line-through mr-1.5">{toTtc({ prix_unitaire: item.prix_origine, tva_percent: item.tva_percent }).toFixed(2)} €</span>
                                <span className="text-emerald-600 font-medium">{toTtc(item).toFixed(2)} €</span>
                                <span className="ml-1.5 px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold" data-testid={`cart-item-remise-badge-${idx}`}>{item.remise_label}</span>
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">{toTtc(item).toFixed(2)} € TTC / unité</p>
                            )}
                          </div>
                          {item.verrouille ? (
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="w-20 text-right font-medium" data-testid={`cart-item-total-${idx}`}>{(toTtc(item) * item.quantite).toFixed(2)} €</span>
                            {!item.sans_suppression && <button onClick={() => removeItem(idx)} className="text-destructive ml-1" data-testid={`cart-item-remove-${idx}`}>×</button>}
                          </div>
                          ) : (
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => openEditPrice(idx)} className="text-muted-foreground hover:text-foreground" title="Modifier le prix" data-testid={`cart-item-editprice-${idx}`}><Pencil size={13} /></button>
                            <button onClick={() => openDiscount(idx)} className="text-muted-foreground hover:text-foreground" title="Appliquer une réduction" data-testid={`cart-item-discount-${idx}`}><Percent size={13} /></button>
                            <button onClick={() => updateQty(idx, -1)} data-testid={`cart-item-minus-${idx}`}><Minus size={13} /></button>
                            <span className="w-6 text-center">{item.quantite}</span>
                            <button onClick={() => updateQty(idx, 1)} data-testid={`cart-item-plus-${idx}`}><Plus size={13} /></button>
                            <span className="w-16 text-right font-medium" data-testid={`cart-item-total-${idx}`}>{(toTtc(item) * item.quantite).toFixed(2)} €</span>
                            <button onClick={() => removeItem(idx)} className="text-destructive ml-1" data-testid={`cart-item-remove-${idx}`}>×</button>
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-sm space-y-1 mb-4">
                <div className="flex justify-between"><span>Total HT</span><span data-testid="cart-total-ht">{ht.toFixed(2)} €</span></div>
                {tvaGroups.map((g) => (
                  <div className="flex justify-between text-muted-foreground" key={g.tva_percent}>
                    <span>dont TVA {g.tva_percent}% (sur {g.base_ht.toFixed(2)} €)</span>
                    <span data-testid={`cart-total-tva-${g.tva_percent}`}>{g.montant_tva.toFixed(2)} €</span>
                  </div>
                ))}
                <div className="flex justify-between font-heading font-bold text-lg"><span>Total TTC</span><span data-testid="cart-total-ttc">{ttc.toFixed(2)} €</span></div>
                {fideliteDiscountAmount > 0 && (
                  <>
                    <div className="flex justify-between text-emerald-600 dark:text-emerald-400" data-testid="cart-fidelite-discount">
                      <span>Réduction fidélité ({selectedPalierPoints} points)</span><span>-{fideliteDiscountAmount.toFixed(2)} €</span>
                    </div>
                    <div className="flex justify-between font-heading font-bold text-lg border-t border-border pt-1" data-testid="cart-total-ttc-effective">
                      <span>À payer</span><span>{ttcEffective.toFixed(2)} €</span>
                    </div>
                  </>
                )}
              </div>

              <Button data-testid="validate-sale-button" className="w-full gap-2" onClick={openPaymentDialog} disabled={cart.length === 0}>
                <Printer size={16} /> Valider &amp; Encaisser
              </Button>

              {shopSettings && !shopSettings.ticket_impression_auto && !shopSettings.activite_exemptee_agec && (
                <p className="text-[11px] text-center text-muted-foreground mt-2" data-testid="agec-ticket-notice">
                  Le ticket de caisse n'est plus imprimé automatiquement : il est remis au client uniquement sur sa
                  demande (loi anti-gaspillage AGEC).
                </p>
              )}

              {lastCreatedDoc && (
                <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 p-2.5" data-testid="post-sale-print-banner">
                  <p className="text-xs text-muted-foreground">
                    {lastCreatedDoc.type === "facture" ? "Facture" : "Ticket"} {lastCreatedDoc.numero} enregistré(e).
                    Le client souhaite un reçu papier ?
                  </p>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                      data-testid="post-sale-print-button"
                      disabled={lastCreatedDoc.paReady === false}
                      title={lastCreatedDoc.paReady === false ? "En attente du PDF officiel de la plateforme agréée" : undefined}
                      onClick={() => printTicketPdf(`${api.defaults.baseURL}/caisse/${lastCreatedDoc.id}/pdf`)}
                    >
                      <Printer size={13} /> Imprimer
                    </Button>
                    <button
                      className="text-muted-foreground hover:text-foreground px-1"
                      data-testid="post-sale-print-dismiss"
                      onClick={() => setLastCreatedDoc(null)}
                      title="Fermer"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </TabsContent>

        {hasPerm(user, "caisse", "voir_vente") && shopSettings?.vente_mode === "rendez_vous" && (
          <TabsContent value="prise-rdv">
            <PriseRdvTab articles={articles} />
          </TabsContent>
        )}

        <Dialog open={!!paBlock} onOpenChange={(v) => { if (!v) setPaBlock(null); }}>
          <DialogContent data-testid="pa-block-dialog">
            <DialogHeader><DialogTitle>Plateforme de facturation électronique injoignable</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">
              {paBlock?.message} La facture n'a pas été émise.
            </p>
            <p className="text-sm text-muted-foreground">
              « Forcer la vente » encaisse maintenant et émet un <strong>ticket provisoire</strong> au nom du client ;
              il faudra le régulariser en facture (bouton facture dans l'historique) dès que la plateforme répond.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPaBlock(null)}>Annuler</Button>
              <Button variant="outline" data-testid="pa-retry" onClick={() => validate(paBlock.finalPayments, paBlock.nonResidentOverride, false)}>Réessayer</Button>
              <Button data-testid="pa-force" onClick={() => validate(paBlock.finalPayments, paBlock.nonResidentOverride, true)}>Forcer la vente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!acompteDialog} onOpenChange={(v) => { if (!v) { setAcompteDialog(null); setAcompteDevisId(null); } }}>
          <DialogContent data-testid="acompte-dialog">
            <DialogHeader><DialogTitle>Acompte — devis n°{acompteDialog?.numero}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Reste à percevoir sur ce devis : {Number(acompteDialog?.reste || 0).toFixed(2)} € TTC. Le ticket portera la ligne « Acompte devis n°{acompteDialog?.numero} » ; l'acompte sera déduit automatiquement de la facture.</p>
            <label className="text-sm">Montant de l'acompte (€ TTC)
              <Input autoFocus data-testid="acompte-montant" inputMode="decimal" value={acompteMontant}
                onChange={(e) => setAcompteMontant(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmAcompte()} />
            </label>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setAcompteDialog(null); setAcompteDevisId(null); }}>Annuler</Button>
              <Button onClick={confirmAcompte} data-testid="acompte-confirm">Ajouter au panier</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!scanChoix} onOpenChange={(v) => !v && setScanChoix(null)}>
          <DialogContent data-testid="scan-choix-dialog">
            <DialogHeader><DialogTitle>Plusieurs articles pour le code {scanChoix?.code}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">Sélectionnez le produit à ajouter au panier.</p>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {(scanChoix?.articles || []).map((a) => (
                <button key={a.id} type="button" data-testid={`scan-choix-${a.id}`}
                  className="w-full flex items-center justify-between rounded-lg border border-border px-3 py-2 text-left hover:border-primary"
                  onClick={() => { const art = a; setScanChoix(null); handleArticleClick(art); }}>
                  <span className="font-medium">{a.nom}<span className="block text-xs text-muted-foreground">{a.categorie || ""} · ID {a.code}{a.stock_infini ? "" : ` · Qté ${a.quantite}`}</span></span>
                  <span className="text-sm font-semibold text-primary">{(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €{a.vendu_au_poids ? `/${uniteDe(a)}` : ""}</span>
                </button>
              ))}
            </div>
            <DialogFooter><Button variant="ghost" onClick={() => setScanChoix(null)}>Annuler</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!poidsDialogArticle} onOpenChange={(v) => !v && setPoidsDialogArticle(null)}>
          <DialogContent data-testid="poids-dialog">
            <DialogHeader><DialogTitle>{poidsDialogArticle?.unite_vente === "m2" || poidsDialogArticle?.unite_vente === "m3" ? "Quantité" : "Peser"} : {poidsDialogArticle?.nom}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">
              Prix : {(poidsDialogArticle?.prix_ttc ?? poidsDialogArticle?.prix ?? 0).toFixed(2)} € / {uniteDe(poidsDialogArticle)}
            </p>
            <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3 text-right" data-testid="poids-input">
              <span className="text-2xl font-bold tabular-nums">{poidsInput || "0"}</span>
              <span className="text-lg font-semibold text-muted-foreground ml-1">{uniteDe(poidsDialogArticle)}</span>
            </div>

            <NumericKeypad
              onDigit={(d) => poidsAppuyer(d)}
              onBackspace={() => poidsAppuyer("back")}
              onDecimal={() => poidsAppuyer(".")}
              showDecimal
              testIdPrefix="poids"
              keyClassName="min-h-[56px] rounded-lg border border-border text-lg font-semibold hover:border-primary active:scale-95 transition-transform duration-100 disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground text-center">Le clavier physique fonctionne aussi (chiffres, virgule, ⌫, ↵).</p>

            {poidsInput && !isNaN(parseFloat(poidsInput.replace(",", "."))) && (
              <p className="text-sm font-semibold" data-testid="poids-total-preview">
                Total : {(parseFloat(poidsInput.replace(",", ".")) * (poidsDialogArticle?.prix_ttc ?? poidsDialogArticle?.prix ?? 0)).toFixed(2)} €
              </p>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPoidsDialogArticle(null)}>Annuler</Button>
              <Button onClick={confirmPoids} data-testid="poids-confirm">Ajouter au panier</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!serviceDialogArticle} onOpenChange={(v) => !v && setServiceDialogArticle(null)}>
          <DialogContent data-testid="service-dialog">
            <DialogHeader><DialogTitle>{serviceDialogArticle?.nom}</DialogTitle></DialogHeader>
            <p className="text-xs text-muted-foreground">
              {(serviceDialogArticle?.prix_ttc ?? serviceDialogArticle?.prix ?? 0).toFixed(2)} €
              {serviceDialogArticle?.duree_minutes ? ` · ${serviceDialogArticle.duree_minutes} min` : ""}
            </p>
            <p className="text-sm text-muted-foreground">
              Vente immédiate de cette prestation, ou prise d'un rendez-vous pour plus tard ?
            </p>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={confirmServiceSansRdv} data-testid="service-sans-rdv">
                Ajouter sans rendez-vous
              </Button>
              <Button onClick={ouvrirRdvDepuisService} className="gap-1.5" data-testid="service-avec-rdv">
                <CalendarDays size={14} />Prendre un rendez-vous
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RdvFormDialog
          open={!!rdvPresetArticle}
          onOpenChange={(v) => !v && setRdvPresetArticle(null)}
          defaultDay={new Date()}
          existing={null}
          presetArticle={rdvPresetArticle}
          allRdv={rdvDuJour}
          serviceArticles={articles.filter((a) => a.type === "service")}
          canAssignOthers={hasPerm(user, "caisse", "assigner_rdv")}
          canSearchClients={hasPerm(user, "client", "view")}
          currentUser={user}
          onSaved={() => { setRdvPresetArticle(null); loadRdvDuJour(); }}
        />

        <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
          <DialogContent data-testid="ventes-force-reopen-dialog">
            <DialogHeader>
              <DialogTitle>Forcer la réouverture de l'onglet Ventes</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground mb-2">
              Un administrateur doit saisir son code pour rouvrir la vente avant l'heure prévue. La réouverture reste
              valable jusqu'à 00h01 cette nuit.
            </p>
            <Input
              type="password"
              inputMode="numeric"
              placeholder="Code administrateur"
              value={reopenPin}
              onChange={(e) => setReopenPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitForceReopen(); } }}
              autoFocus
              data-testid="ventes-force-reopen-pin-input"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setReopenDialogOpen(false)}>Annuler</Button>
              <Button onClick={submitForceReopen} disabled={reopenSubmitting} data-testid="ventes-force-reopen-submit">
                {reopenSubmitting ? "Vérification..." : "Rouvrir"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TabsContent value="carte-cadeau">
          <div className="flex flex-wrap gap-3 mb-4">
            <Button className="gap-2" onClick={() => setGenererDialogOpen(true)} data-testid="carte-cadeau-generer-button">
              <Star size={16} />Générer une carte cadeau
            </Button>
            {fidelite?.active && (
              <Button variant="outline" className="gap-2" onClick={() => setViaFideliteDialogOpen(true)} data-testid="carte-cadeau-via-fidelite-button">
                <Star size={16} />Carte cadeau via fidélité
              </Button>
            )}
          </div>

          <div className="relative mb-4 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Rechercher par n° de carte ou nom du vendeur..."
              value={carteCadeauSearch}
              onChange={(e) => setCarteCadeauSearch(e.target.value)}
              className="pl-9"
              data-testid="carte-cadeau-search-input"
            />
          </div>

          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="cartes-cadeaux-list">
              <thead className="bg-secondary text-left">
                <tr>
                  <th className="p-3">Code</th><th className="p-3">Montant</th><th className="p-3">Origine</th>
                  <th className="p-3">Validité</th><th className="p-3">Statut</th><th className="p-3">Date</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {cartesCadeaux.length === 0 && (
                  <tr><td colSpan={7} className="p-4 text-center text-muted-foreground" data-testid="cartes-cadeaux-empty">Aucune carte cadeau générée pour l'instant.</td></tr>
                )}
                {cartesCadeaux
                  .filter((c) => {
                    const q = carteCadeauSearch.trim().toLowerCase();
                    if (!q) return true;
                    return c.code.toLowerCase().includes(q) || (c.vendeur_nom || "").toLowerCase().includes(q);
                  })
                  .map((c) => (
                  <tr key={c.id} className={`border-t border-border ${["annulee", "expiree", "utilisee"].includes(c.statut) ? "opacity-60" : ""}`} data-testid={`carte-cadeau-row-${c.id}`}>
                    <td className="p-3 font-mono">{c.code}</td>
                    <td className="p-3 font-semibold">{c.montant.toFixed(2)} € <span className="text-xs text-muted-foreground font-normal">({c.solde.toFixed(2)} € restant)</span></td>
                    <td className="p-3 text-muted-foreground">{c.source === "fidelite" ? `Fidélité (${c.fidelite_points_used} pts)` : c.source === "avoir" ? "Avoir" : "Manuelle"}</td>
                    <td className="p-3">
                      {c.validite_type === "lifetime" ? (
                        <span className="text-muted-foreground">À vie</span>
                      ) : c.expiree ? (
                        <span className="text-destructive">Expirée le {c.valide_jusquau}</span>
                      ) : (
                        <span>Jusqu'au {c.valide_jusquau}</span>
                      )}
                    </td>
                    <td className="p-3" data-testid={`carte-cadeau-statut-${c.id}`}>
                      {c.statut === "annulee" && (
                        <span className="flex items-center gap-1 text-xs text-destructive" title={`${c.annulee_motif} — par ${c.annulee_par}`}>
                          <Ban size={12} />Annulée
                        </span>
                      )}
                      {c.statut === "expiree" && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Ban size={12} />Expirée</span>
                      )}
                      {c.statut === "utilisee" && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground"><Ban size={12} />Utilisée</span>
                      )}
                      {c.statut === "partiellement_utilisee" && (
                        <span className="text-xs text-amber-600">Partiellement utilisée</span>
                      )}
                      {c.statut === "active" && (
                        <span className="text-xs text-emerald-600">Active</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{c.date} {c.heure}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2.5">
                        <a href={`${api.defaults.baseURL}/caisse/cartes-cadeaux/${c.id}/pdf`} target="_blank" rel="noreferrer" title="PDF" data-testid={`carte-cadeau-pdf-${c.id}`}>
                          <FileDown size={15} className="text-muted-foreground" />
                        </a>
                        {["active", "partiellement_utilisee"].includes(c.statut) && canFiscal && (
                          <button onClick={() => { setAnnulerCarte(c); setAnnulerCarteMotif(""); }} title="Annuler cette carte cadeau" data-testid={`carte-cadeau-annuler-${c.id}`}>
                            <Ban size={15} className="text-destructive" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <Dialog open={!!annulerCarte} onOpenChange={(v) => !v && setAnnulerCarte(null)}>
          <DialogContent data-testid="carte-cadeau-annuler-dialog">
            <DialogHeader><DialogTitle>Annuler la carte cadeau {annulerCarte?.code}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-2">
              Cette carte ne sera jamais supprimée : elle reste consultable dans l'historique, marquée comme annulée,
              avec le motif et l'auteur de l'annulation. Un motif est obligatoire.
            </p>
            <Textarea
              placeholder="Motif de l'annulation (obligatoire) — ex. carte perdue, émise par erreur..."
              value={annulerCarteMotif}
              onChange={(e) => setAnnulerCarteMotif(e.target.value)}
              rows={3}
              autoFocus
              data-testid="carte-cadeau-annuler-motif"
            />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAnnulerCarte(null)}>Retour</Button>
              <Button variant="destructive" onClick={submitAnnulerCarte} disabled={annulerCarteSubmitting} data-testid="carte-cadeau-annuler-submit">
                {annulerCarteSubmitting ? "Annulation..." : "Confirmer l'annulation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={genererDialogOpen} onOpenChange={setGenererDialogOpen}>
          <DialogContent data-testid="carte-cadeau-generer-dialog">
            <DialogHeader><DialogTitle>Générer une carte cadeau</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Montant (€)</label>
                <Input type="number" min="0.01" step="0.01" value={genererMontant} onChange={(e) => setGenererMontant(e.target.value)} autoFocus data-testid="carte-cadeau-montant-input" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Durée de validité (jours) — vide = valable à vie</label>
                <Input type="number" min="1" placeholder="Vide = à vie" value={genererValiditeJours} onChange={(e) => setGenererValiditeJours(e.target.value)} data-testid="carte-cadeau-validite-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setGenererDialogOpen(false)}>Annuler</Button>
              <Button onClick={submitGenererCarte} disabled={genererSubmitting} data-testid="carte-cadeau-generer-submit">
                {genererSubmitting ? "Génération..." : "Générer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={viaFideliteDialogOpen} onOpenChange={(v) => { setViaFideliteDialogOpen(v); if (!v) { setViaFideliteClient(null); setViaFideliteBalance(null); } }}>
          <DialogContent data-testid="carte-cadeau-via-fidelite-dialog">
            <DialogHeader><DialogTitle>Carte cadeau via points de fidélité</DialogTitle></DialogHeader>
            {!viaFideliteClient ? (
              <div className="relative">
                <Input
                  placeholder="Rechercher un client..."
                  value={viaFideliteClientSearch}
                  onChange={(e) => setViaFideliteClientSearch(e.target.value)}
                  autoFocus
                  data-testid="carte-cadeau-via-fidelite-client-search"
                />
                {viaFideliteClientResults.length > 0 && (
                  <div className="mt-1 rounded-lg border border-border bg-card shadow-md max-h-48 overflow-y-auto" data-testid="carte-cadeau-via-fidelite-client-results">
                    {viaFideliteClientResults.map((c) => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        onClick={() => selectViaFideliteClient(c)}
                        data-testid={`carte-cadeau-via-fidelite-client-option-${c.id}`}
                      >
                        {c.prenom} {c.nom} {c.telephone ? `· ${c.telephone}` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm">
                    <span className="font-semibold">{viaFideliteClient.prenom} {viaFideliteClient.nom}</span>
                    {" — "}
                    <span data-testid="carte-cadeau-via-fidelite-balance">{viaFideliteBalance ?? "..."} points</span>
                  </p>
                  <button onClick={() => { setViaFideliteClient(null); setViaFideliteBalance(null); }} className="text-muted-foreground hover:text-foreground" data-testid="carte-cadeau-via-fidelite-client-clear">
                    <X size={14} />
                  </button>
                </div>
                {viaFideliteBalance !== null && (
                  <div className="space-y-2" data-testid="carte-cadeau-via-fidelite-paliers">
                    {(fidelite?.paliers_cartes_cadeaux || []).length === 0 && (
                      <p className="text-sm text-muted-foreground">Aucun palier configuré (Administration &gt; Fidélité).</p>
                    )}
                    {(fidelite?.paliers_cartes_cadeaux || [])
                      .slice()
                      .sort((a, b) => a.points - b.points)
                      .map((p) => {
                        const eligible = viaFideliteBalance >= p.points;
                        return (
                          <button
                            key={p.points}
                            disabled={!eligible || viaFideliteSubmitting}
                            onClick={() => submitCarteViaFidelite(p.points)}
                            className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-left transition-colors ${eligible ? "border-primary hover:bg-primary/5" : "border-border opacity-50 cursor-not-allowed"}`}
                            data-testid={`carte-cadeau-via-fidelite-palier-${p.points}`}
                          >
                            <span>{p.points} points</span>
                            <span className="font-semibold">{p.montant.toFixed(2)} € de carte cadeau</span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {canFiscal && (
          <TabsContent value="fiscal">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Intégrité du registre</p>
                {chainStatus ? (
                  <div className="flex items-center gap-2">
                    {chainStatus.valid ? <ShieldCheck size={20} className="text-emerald-600" /> : <ShieldAlert size={20} className="text-destructive" />}
                    <div>
                      <p className="font-semibold">{chainStatus.valid ? "Chaîne intègre" : "Anomalie détectée"}</p>
                      <p className="text-xs text-muted-foreground">{chainStatus.checked} événement(s) vérifié(s)</p>
                    </div>
                  </div>
                ) : <p className="text-sm text-muted-foreground">—</p>}
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Cumul perpétuel TTC</p>
                <p className="text-2xl font-heading font-bold">{(cumuls?.total_ttc || 0).toFixed(2)} €</p>
                <p className="text-xs text-muted-foreground">{cumuls?.nb_documents || 0} document(s) clôturé(s) au total</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4 flex flex-col gap-2 justify-center">
                <Button size="sm" variant="outline" className="gap-2" onClick={exportArchive} data-testid="export-archive-button">
                  <FileArchive size={14} /> Export archive fiscale (JSON)
                </Button>
                <Button size="sm" variant="outline" className="gap-2 w-full" onClick={viewAttestation} data-testid="attestation-button">
                  <FileBadge size={14} /> Attestation signée
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold flex items-center gap-2 mb-1"><Mail size={15} />Envoi automatique des clôtures</p>
              <p className="text-xs text-muted-foreground mb-3">
                Les clôtures journalière (chaque nuit, pour la journée qui vient de se terminer), mensuelle (le 1er
                du mois, pour le mois qui vient de se terminer) et annuelle (le 1er janvier, pour l'année qui vient
                de se terminer) sont réalisées automatiquement peu après minuit (00h15, heure de Paris) — jamais
                avant la fin réelle de la période. Une archive ZIP complète (ticket Z, PDF de chaque document de la
                période, et leurs données brutes vérifiables) est alors envoyée à chacun des destinataires ci-dessous.
              </p>
              {canManageFiscal ? (
                <>
                  <Button size="sm" variant="outline" className="gap-2" onClick={() => setComptaEmailMenuOpen(true)} data-testid="compta-email-menu-button">
                    <Mail size={14} />
                    {comptaEmails.length === 0 ? "Ajouter un destinataire" : `${comptaEmails.length} destinataire${comptaEmails.length > 1 ? "s" : ""}`}
                  </Button>
                  <Dialog open={comptaEmailMenuOpen} onOpenChange={setComptaEmailMenuOpen}>
                    <DialogContent data-testid="compta-email-menu-dialog">
                      <DialogHeader>
                        <DialogTitle>Destinataires des clôtures automatiques</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-2">
                        {comptaEmails.length === 0 && (
                          <p className="text-sm text-muted-foreground" data-testid="compta-email-list-empty">Aucun destinataire pour l'instant.</p>
                        )}
                        {comptaEmails.map((email, i) => (
                          <div key={i} className="flex items-center gap-2" data-testid={`compta-email-row-${i}`}>
                            {editingComptaEmailIndex === i ? (
                              <>
                                <Input
                                  type="email"
                                  value={editingComptaEmailValue}
                                  onChange={(e) => setEditingComptaEmailValue(e.target.value)}
                                  className="flex-1"
                                  autoFocus
                                  data-testid={`compta-email-edit-input-${i}`}
                                />
                                <Button size="sm" disabled={comptaEmailSaving} onClick={saveEditComptaEmail} data-testid={`compta-email-edit-save-${i}`}>OK</Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingComptaEmailIndex(null)}>Annuler</Button>
                              </>
                            ) : (
                              <>
                                <p className="flex-1 text-sm truncate">{email}</p>
                                <button type="button" onClick={() => startEditComptaEmail(i)} title="Modifier" data-testid={`compta-email-edit-${i}`}>
                                  <Pencil size={15} className="text-muted-foreground hover:text-foreground" />
                                </button>
                                <button type="button" onClick={() => removeComptaEmail(i)} title="Supprimer" data-testid={`compta-email-delete-${i}`}>
                                  <Trash2 size={15} className="text-destructive" />
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pt-3 mt-2 border-t border-border">
                        <Input
                          type="email"
                          placeholder="ajouter une adresse (ex: compta@votre-societe.fr)"
                          value={newComptaEmail}
                          onChange={(e) => setNewComptaEmail(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComptaEmail(); } }}
                          className="flex-1"
                          data-testid="compta-email-new-input"
                        />
                        <Button size="sm" disabled={comptaEmailSaving || !newComptaEmail.trim()} onClick={addComptaEmail} className="gap-1.5" data-testid="compta-email-add-button">
                          <Plus size={14} />Ajouter
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="compta-email-readonly">
                  {comptaEmails.length > 0 ? comptaEmails.join(", ") : "Aucune adresse renseignée."}
                </p>
              )}

              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-medium mb-1">Empreinte fiscale externe — second destinataire indépendant</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Chaque mois, une empreinte de sécurité du registre est envoyée par e-mail à l'adresse comptable
                  ci-dessus. Renseigner ici un second destinataire (avocat, tiers de confiance...), <strong>indépendant
                  du comptable habituel</strong>, donne une preuve datée plus solide en cas de contestation impliquant
                  le comptable lui-même. Optionnel.
                </p>
                {canManageFiscal ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="email"
                      placeholder="second destinataire (optionnel)"
                      value={anchorEmailSecondaire}
                      onChange={(e) => setAnchorEmailSecondaire(e.target.value)}
                      className="max-w-sm"
                      data-testid="anchor-email-secondaire-input"
                    />
                    <Button size="sm" variant="outline" disabled={comptaEmailSaving} onClick={saveAnchorEmailSecondaire} data-testid="anchor-email-secondaire-save-button">
                      {comptaEmailSaving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground" data-testid="anchor-email-secondaire-readonly">{anchorEmailSecondaire || "Aucune adresse renseignée."}</p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold flex items-center gap-2 mb-1"><Lock size={15} />Verrouillage de l'onglet Ventes</p>
              <p className="text-xs text-muted-foreground mb-3">
                Réglage purement opérationnel, indépendant de la clôture fiscale ci-dessus (qui reste, elle, toujours
                effectuée après minuit pour ne jamais manquer de vente) : passé l'heure choisie, l'onglet "Vente" se
                bloque pour tout le monde jusqu'à 00h01 le lendemain matin. Un administrateur peut forcer la
                réouverture à tout moment depuis l'onglet Vente verrouillé.
              </p>
              {canManageFiscal ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="time"
                    value={lockHourInput}
                    onChange={(e) => setLockHourInput(e.target.value)}
                    className="max-w-[160px]"
                    data-testid="ventes-lock-hour-input"
                  />
                  <Button size="sm" variant="outline" disabled={lockHourSaving} onClick={saveLockHour} data-testid="ventes-lock-hour-save-button">
                    {lockHourSaving ? "Enregistrement..." : "Enregistrer"}
                  </Button>
                  {ventesLock?.enabled && (
                    <Button
                      size="sm" variant="ghost" className="text-muted-foreground"
                      disabled={lockHourSaving}
                      onClick={() => { setLockHourInput(""); saveLockHour(); }}
                      data-testid="ventes-lock-hour-disable-button"
                    >
                      Désactiver le verrouillage
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {ventesLock?.enabled ? `Verrouillage actif à partir de ${ventesLock.lock_hour}.` : "Aucun verrouillage configuré."}
                </p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold flex items-center gap-2 mb-1"><FileCheck2 size={15} />Affichage des prix sur le ticket</p>
              <p className="text-xs text-muted-foreground mb-3">
                Choisissez si le prix affiché en face de chaque article sur le ticket de caisse est HT (hors taxes)
                ou TTC (montant réellement payé). Les totaux HT/TVA/TTC généraux du document ne changent jamais,
                quel que soit ce réglage — seul le montant imprimé par ligne d'article change.
              </p>
              {canManageFiscal ? (
                <Select value={ticketPrixAffichage} onValueChange={saveTicketPrixAffichage} disabled={ticketPrixAffichageSaving}>
                  <SelectTrigger className="w-56" data-testid="ticket-prix-affichage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ttc">Prix TTC (par défaut)</SelectItem>
                    <SelectItem value="ht">Prix HT</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground">{ticketPrixAffichage === "ht" ? "Prix HT" : "Prix TTC"}</p>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold flex items-center gap-2 mb-1"><Star size={15} />Programme de fidélité</p>
              <p className="text-xs text-muted-foreground">
                {fidelite?.active
                  ? "Programme actif — points gagnés par euro dépensé et valeur du point réglables depuis Administration > Fidélité."
                  : "Programme non activé — à activer depuis Administration > Fidélité."}
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold mb-3">Clôtures (ticket Z)</p>
              <p className="text-xs text-muted-foreground mb-4">
                La clôture se fait automatiquement, chaque nuit peu après minuit (heure de Paris), une fois la
                journée entièrement terminée — aucune action manuelle n'est nécessaire ni possible pour une
                période en cours. En cas de période oubliée ou clôturée avec une erreur, utilisez la
                régularisation antérieure ci-dessous.
              </p>
              <div className="space-y-1.5" data-testid="clotures-list">
                {clotures.length === 0 && <p className="text-sm text-muted-foreground">Aucune clôture effectuée pour le moment.</p>}
                {clotures.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between text-sm py-2 px-3 rounded-lg border border-border gap-2" data-testid={`cloture-row-${c.id}`}>
                    <div className="min-w-0">
                      <span className="font-medium">{c.periode_label}</span>
                      {c.complementaire && (
                        <span className="ml-1.5 px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-semibold" data-testid={`cloture-complement-badge-${c.id}`}>
                          Régularisation
                        </span>
                      )}
                      {formatPeriodeCouverte(c) && (
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-secondary text-[10px] font-medium" data-testid={`cloture-periode-${c.id}`}>
                          {formatPeriodeCouverte(c)}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-2">Clôturée le {new Date(c.created_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}</span>
                      <span className="text-xs text-muted-foreground block">{c.nb_documents} document(s) · Séq. n°{c.seq}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{c.total_ttc?.toFixed(2)} €</span>
                      <a href={`${api.defaults.baseURL}/caisse/cloture/${c.id}/pdf`} target="_blank" rel="noreferrer" title="Ticket Z seul (aperçu PDF)" data-testid={`cloture-pdf-${c.id}`}>
                        <FileDown size={15} className="text-muted-foreground" />
                      </a>
                      <a href={`${api.defaults.baseURL}/caisse/cloture/${c.id}/export`} title="Archive complète (ZIP : ticket Z + PDF des documents + données JSON)" data-testid={`cloture-export-${c.id}`}>
                        <FileArchive size={15} className="text-primary" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {canManageFiscal && (
                <div className="mt-5 pt-4 border-t border-border">
                  <p className="font-heading font-semibold mb-1">Clôture antérieure / régularisation</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Pour un jour, un mois ou une année oublié, ou clôturé trop tôt / avec une erreur. La clôture
                    d'origine n'est jamais modifiée : s'il reste des documents non clôturés sur cette période, un
                    complément chaîné et référencé lui est automatiquement associé.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant={pastPeriode === "jour" ? "default" : "outline"} onClick={() => { setPastPeriode("jour"); setPastDate(""); }} data-testid="past-periode-jour">Jour</Button>
                      <Button type="button" size="sm" variant={pastPeriode === "mois" ? "default" : "outline"} onClick={() => { setPastPeriode("mois"); setPastDate(""); }} data-testid="past-periode-mois">Mois</Button>
                      <Button type="button" size="sm" variant={pastPeriode === "annee" ? "default" : "outline"} onClick={() => { setPastPeriode("annee"); setPastDate(""); }} data-testid="past-periode-annee">Année</Button>
                    </div>
                    {pastPeriode === "jour" && (
                      <Input type="date" value={pastDate} onChange={(e) => setPastDate(e.target.value)} className="w-40" data-testid="past-date-input" />
                    )}
                    {pastPeriode === "mois" && (
                      <Input type="month" value={pastDate} onChange={(e) => setPastDate(e.target.value)} className="w-40" data-testid="past-date-input" />
                    )}
                    {pastPeriode === "annee" && (
                      <Input type="number" placeholder="AAAA" min="2000" max="2100" value={pastDate} onChange={(e) => setPastDate(e.target.value)} className="w-28" data-testid="past-date-input" />
                    )}
                    <Button size="sm" variant="outline" onClick={doClotureAnterieure} data-testid="past-cloture-button">Clôturer cette période</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 mb-6">
              <p className="font-heading font-semibold mb-1 flex items-center gap-2"><Wallet size={15} />Fond de caisse — historique des sessions</p>
              <p className="text-xs text-muted-foreground mb-3">
                Comptages d'ouverture/fermeture de caisse (contrôle interne anti-fraude, distinct du registre fiscal
                des ventes lui-même). Un écart significatif entre le montant théorique et le comptage réel mérite
                une explication (voir les notes de chaque session).
              </p>
              <div className="space-y-1.5" data-testid="cash-session-history-list">
                {sessionHistory.length === 0 && <p className="text-sm text-muted-foreground">Aucune session de caisse enregistrée pour le moment.</p>}
                {sessionHistory.map((s) => {
                  const ecart = s.ecart;
                  const hasEcart = ecart !== null && ecart !== undefined && Math.abs(ecart) >= 0.01;
                  return (
                    <div key={s.id} className={`flex flex-wrap items-center justify-between text-sm py-2 px-3 rounded-lg border gap-2 ${hasEcart ? "border-amber-500/40 bg-amber-500/5" : "border-border"}`} data-testid={`cash-session-row-${s.id}`}>
                      <div className="min-w-0">
                        <span className="font-medium">
                          {shopSettings?.caisse_mode === "multiple" && s.caisse_numero && `Caisse ${s.caisse_numero} — `}
                          {new Date(s.ouverte_le).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}
                        </span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {s.status === "ouverte" ? "En cours" : `Fermée ${new Date(s.fermee_le).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}`}
                        </span>
                        <span className="text-xs text-muted-foreground block">
                          Ouverte par {s.ouverte_par_nom}{s.status !== "ouverte" && ` · Fermée par ${s.fermee_par_nom}`}
                        </span>
                        {s.notes && <span className="text-xs text-muted-foreground block italic">{s.notes}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Fond départ : {s.fond_ouverture?.toFixed(2)} €</p>
                          {s.status !== "ouverte" && (
                            <>
                              <p className="text-xs text-muted-foreground">Théorique : {s.fond_fermeture_theorique?.toFixed(2)} € · Réel : {s.fond_fermeture_reel?.toFixed(2)} €</p>
                              <p className={`text-sm font-semibold ${hasEcart ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`} data-testid={`cash-session-ecart-${s.id}`}>
                                Écart : {ecart > 0 ? "+" : ""}{ecart?.toFixed(2)} €
                              </p>
                            </>
                          )}
                        </div>
                        {/* Code PIN oublié = caisse bloquée pour rejoindre (demande
                            explicite) : permet de le retirer sans fermer la caisse. */}
                        {s.status === "ouverte" && s.pin_set && canManageFiscal && (
                          <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => resetSessionPin(s)} data-testid={`cash-session-reset-pin-button-${s.id}`}>
                            <KeyRound size={13} /> Réinitialiser le PIN
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => openJournal(s)} data-testid={`cash-session-journal-button-${s.id}`}>
                          <ScrollText size={13} /> Journal
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={!!annulerTicket} onOpenChange={(v) => !v && setAnnulerTicket(null)}>
        <DialogContent data-testid="ticket-annuler-dialog">
          <DialogHeader><DialogTitle>Annuler {annulerTicket?.numero}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Le document sera marqué "annulé" et tracé dans le registre fiscal — il ne sera jamais supprimé.
          </p>
          <Textarea data-testid="annuler-motif-input" placeholder="Motif de l'annulation (obligatoire)" value={annulerMotif} onChange={(e) => setAnnulerMotif(e.target.value)} />
          <DialogFooter>
            <Button variant="destructive" data-testid="annuler-confirm-button" onClick={confirmAnnuler}>Confirmer l'annulation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!avoirTicket} onOpenChange={(v) => !v && setAvoirTicket(null)}>
        <DialogContent data-testid="avoir-dialog">
          <DialogHeader><DialogTitle>Avoir {avoirPartiel ? "partiel" : ""} sur {avoirTicket?.numero} — {(avoirPartiel ? avoirMontantPartiel() : avoirTicket?.total_ttc || 0).toFixed(2)} €</DialogTitle></DialogHeader>

          <div className="mb-3">
            <Button type="button" size="sm" variant={avoirPartiel ? "default" : "outline"} data-testid="avoir-partiel-toggle"
              onClick={() => { setAvoirPartiel(!avoirPartiel); setAvoirLignes({}); }}>
              {avoirPartiel ? "Avoir partiel (cliquer pour un avoir total)" : "Rembourser seulement certaines lignes"}
            </Button>
            {avoirPartiel && (
              <div className="mt-2 space-y-1 rounded-md border p-2" data-testid="avoir-lignes">
                {(avoirTicket?.items || []).map((it, idx) => (it.prix_unitaire > 0 && it.type !== "acompte_deduction" && it.type !== "reprise_deduction") ? (
                  <div key={idx} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{it.nom} <span className="text-muted-foreground">(vendu : {it.quantite})</span></span>
                    <Input type="number" min="0" max={it.quantite} step="any" className="w-24 h-8"
                      data-testid={`avoir-ligne-${idx}`}
                      value={avoirLignes[idx] ?? ""} placeholder="Qté"
                      onChange={(e) => setAvoirLignes({ ...avoirLignes, [idx]: e.target.value })} />
                  </div>
                ) : null)}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-3">
            <Button
              type="button" size="sm"
              variant={!avoirEmettreCarteCadeau ? "default" : "outline"}
              onClick={() => setAvoirEmettreCarteCadeau(false)}
              data-testid="avoir-type-remboursement"
            >
              Remboursement classique
            </Button>
            <Button
              type="button" size="sm"
              variant={avoirEmettreCarteCadeau ? "default" : "outline"}
              onClick={() => setAvoirEmettreCarteCadeau(true)}
              className="gap-1.5"
              data-testid="avoir-type-carte-cadeau"
            >
              <Star size={14} />Émettre une carte cadeau
            </Button>
          </div>

          {avoirEmettreCarteCadeau ? (
            <div className="mb-2">
              <p className="text-sm text-muted-foreground mb-2">
                Une carte cadeau de {avoirTicket?.total_ttc?.toFixed(2)} € sera générée et remise au client à la
                place d'un remboursement en espèces/carte. {avoirTicket?.client_id && "Elle sera aussi rattachée à sa fiche client."}
              </p>
              <label className="text-xs text-muted-foreground block mb-1">Durée de validité (jours) — vide = valable à vie</label>
              <Input
                type="number" min="1" placeholder="Vide = à vie"
                value={avoirCarteCadeauValidite}
                onChange={(e) => setAvoirCarteCadeauValidite(e.target.value)}
                data-testid="avoir-carte-cadeau-validite"
              />
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-2">
                Indiquez le moyen par lequel le client a réellement été remboursé : c'est nécessaire pour que le fond de
                caisse (session en cours) reflète correctement une sortie d'espèces du tiroir.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {paymentMethods.filter((m) => m.key !== "carte_cadeau").map((m) => {
                  const Icon = paymentMethodIcon(m.key);
                  const disabled = m.key === "espece" && !currentSession;
                  return (
                    <Button
                      key={m.key}
                      type="button"
                      variant={avoirMode === m.key ? "default" : "outline"}
                      className="gap-2 justify-start"
                      disabled={disabled}
                      title={disabled ? "Ouvrez la caisse (fond de caisse) pour rembourser en espèces" : undefined}
                      onClick={() => setAvoirMode(m.key)}
                      data-testid={`avoir-mode-${m.key}`}
                    >
                      <Icon size={15} /> {m.label}
                    </Button>
                  );
                })}
              </div>
              {paymentMethods.find((m) => m.key === avoirMode)?.requires_cheque_info && (
                <div className="space-y-2 mb-2">
                  <Input data-testid="avoir-cheque-numero" placeholder="N° de chèque" value={avoirChequeNumero} onChange={(e) => setAvoirChequeNumero(e.target.value)} />
                  <Input data-testid="avoir-cheque-nom" placeholder="Nom du titulaire" value={avoirChequeNom} onChange={(e) => setAvoirChequeNom(e.target.value)} />
                </div>
              )}
            </>
          )}
          <Textarea
            data-testid="avoir-motif"
            placeholder="Motif de l'avoir (recommandé — ex. produit défectueux, erreur de caisse...)"
            value={avoirMotif}
            onChange={(e) => setAvoirMotif(e.target.value)}
            className="mb-2"
            rows={2}
          />
          <DialogFooter>
            <Button data-testid="avoir-confirm-button" disabled={avoirSaving} onClick={confirmAvoir}>
              {avoirSaving ? "Émission..." : "Émettre l'avoir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!facturerTicket} onOpenChange={(v) => !v && setFacturerTicket(null)}>
        <DialogContent data-testid="ticket-facturer-dialog">
          <DialogHeader><DialogTitle>Duplicata facture de {facturerTicket?.numero}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Une facture reprenant fidèlement le contenu du ticket {facturerTicket?.numero} sera générée. Le ticket
            reste inchangé ; ce duplicata n'est pas recompté dans le chiffre d'affaires (même vente).
          </p>
          <div className="space-y-2">
            <Input data-testid="facturer-client-nom" placeholder="Nom du client (obligatoire)" value={facturerInfo.nom} onChange={(e) => setFacturerInfo({ ...facturerInfo, nom: e.target.value })} />
            <Input data-testid="facturer-client-adresse" placeholder="Adresse" value={facturerInfo.adresse} onChange={(e) => setFacturerInfo({ ...facturerInfo, adresse: e.target.value })} />
            <Input data-testid="facturer-client-email" placeholder="Email" value={facturerInfo.email} onChange={(e) => setFacturerInfo({ ...facturerInfo, email: e.target.value })} />
            <Input data-testid="facturer-client-siret" placeholder="N° SIRET client (optionnel)" value={facturerInfo.siret} onChange={(e) => setFacturerInfo({ ...facturerInfo, siret: e.target.value })} />
            <Input data-testid="facturer-client-siren" placeholder="N° SIREN client (obligatoire si professionnel assujetti à la TVA)" value={facturerInfo.siren} onChange={(e) => setFacturerInfo({ ...facturerInfo, siren: e.target.value })} />
            <Input data-testid="facturer-client-tva-intracom" placeholder="N° TVA intracommunautaire client (optionnel, UE)" value={facturerInfo.tva_intracom} onChange={(e) => setFacturerInfo({ ...facturerInfo, tva_intracom: e.target.value })} />
            <Input data-testid="facturer-client-adresse-livraison" placeholder="Adresse de livraison (si différente, optionnel)" value={facturerInfo.adresse_livraison} onChange={(e) => setFacturerInfo({ ...facturerInfo, adresse_livraison: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="facturer-confirm-button" onClick={confirmFacturer}>Émettre le duplicata facture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ShareDialog open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)} module="caisse" itemId={shareItem} onShared={load} />

      <Dialog open={!!mailTicket} onOpenChange={(v) => !v && setMailTicket(null)}>
        <DialogContent data-testid="ticket-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer {mailTicket?.type === "facture" ? "la facture" : "le ticket"} {mailTicket?.numero} par e-mail</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Adresse e-mail du client" value={mailTo} onChange={(e) => setMailTo(e.target.value)} data-testid="ticket-mail-to" />
            <Input placeholder="Objet" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} data-testid="ticket-mail-subject" />
            <Textarea className="h-32" placeholder="Message" value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} data-testid="ticket-mail-message" />
            <p className="text-xs text-muted-foreground">Le PDF sera joint automatiquement. L'e-mail sera envoyé depuis l'adresse configurée pour votre boutique (Admin &gt; Boutiques &amp; Dépôts).</p>
          </div>
          <DialogFooter>
            <Button onClick={sendTicketMail} disabled={mailSending} className="gap-2" data-testid="ticket-mail-send-button">
              <Mail size={16} />{mailSending ? "Envoi..." : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editPriceIdx !== null} onOpenChange={(v) => !v && setEditPriceIdx(null)}>
        <DialogContent data-testid="edit-price-dialog">
          <DialogHeader><DialogTitle>Modifier le prix — {editPriceIdx !== null && cart[editPriceIdx]?.nom}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Ce nouveau prix TTC ne s'applique qu'à cette vente ; le prix de l'article dans le stock n'est pas modifié.
          </p>
          <Input
            data-testid="edit-price-input"
            type="number"
            step="0.01"
            min="0"
            autoFocus
            placeholder="Nouveau prix unitaire TTC €"
            value={editPriceValue}
            onChange={(e) => setEditPriceValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmEditPrice()}
          />
          <DialogFooter>
            <Button data-testid="edit-price-confirm-button" onClick={confirmEditPrice}>Appliquer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={discountIdx !== null} onOpenChange={(v) => !v && setDiscountIdx(null)}>
        <DialogContent data-testid="discount-dialog">
          <DialogHeader><DialogTitle>Réduction — {discountIdx !== null && cart[discountIdx]?.nom}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground mb-2">
            Calculée sur le prix d'origine ({discountIdx !== null ? toTtc({ prix_unitaire: cart[discountIdx]?.prix_origine ?? cart[discountIdx]?.prix_unitaire ?? 0, tva_percent: cart[discountIdx]?.tva_percent }).toFixed(2) : "0.00"} € TTC).
            Elle apparaîtra en clair sur le ticket ou la facture.
          </p>
          <div className="flex gap-2 mb-3">
            <Button type="button" size="sm" variant={discountType === "percent" ? "default" : "outline"} onClick={() => setDiscountType("percent")} data-testid="discount-type-percent">% Pourcentage</Button>
            <Button type="button" size="sm" variant={discountType === "amount" ? "default" : "outline"} onClick={() => setDiscountType("amount")} data-testid="discount-type-amount">€ Montant</Button>
          </div>
          <Input
            data-testid="discount-value-input"
            type="number"
            step="0.01"
            min="0"
            autoFocus
            placeholder={discountType === "percent" ? "Réduction en %" : "Réduction en €"}
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmDiscount()}
          />
          {discountPreview() !== null && (
            <p className="text-sm mt-2">
              Nouveau prix TTC : <span className="font-semibold text-emerald-600" data-testid="discount-preview-price">{discountPreview().toFixed(2)} €</span>
            </p>
          )}
          <DialogFooter>
            <Button data-testid="discount-confirm-button" onClick={confirmDiscount}>Appliquer la réduction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue d'encaissement : choix du/des moyen(s) de paiement, montant remis,
          calcul du rendu de monnaie (espèces) et du reste à payer (paiement partagé
          entre plusieurs moyens). Toutes les lignes de règlement apparaîtront sur le
          ticket/la facture imprimée. */}
      <Dialog open={paymentDialogOpen} onOpenChange={(v) => !v && !validating && setPaymentDialogOpen(false)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Encaissement</DialogTitle></DialogHeader>

          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Total à payer</span>
            <span className="font-semibold" data-testid="payment-total-ttc">{ttcEffective.toFixed(2)} €</span>
          </div>
          {fideliteDiscountAmount > 0 && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-2">
              (dont -{fideliteDiscountAmount.toFixed(2)} € de réduction fidélité, {selectedPalierPoints} points)
            </p>
          )}

          {payments.length > 0 && (
            <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto" data-testid="payment-list">
              {payments.map((p, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm rounded-lg border border-border bg-secondary/40 px-3 py-1.5" data-testid={`payment-row-${idx}`}>
                  <div className="min-w-0">
                    <p className="truncate">{p.label}</p>
                    {p.mode === "cheque" && (
                      <p className="text-xs text-muted-foreground truncate">N° {p.cheque_numero} — {p.cheque_nom}</p>
                    )}
                    {p.rendu_monnaie > 0 && (
                      <p className="text-xs text-emerald-600">Rendu monnaie : {p.rendu_monnaie.toFixed(2)} €</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-medium" data-testid={`payment-row-montant-${idx}`}>{p.montant.toFixed(2)} €</span>
                    <button onClick={() => removePayment(idx)} className="text-destructive" data-testid={`payment-row-remove-${idx}`} title="Retirer ce paiement">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-between text-sm mb-3 pt-2 border-t border-border">
            <span className="text-muted-foreground">Réglé</span>
            <span data-testid="payment-total-regle">{totalRegle.toFixed(2)} €</span>
          </div>
          <div className={`flex justify-between font-heading font-bold text-lg mb-4 ${resteAPayer > 0 ? "" : "text-emerald-600"}`}>
            <span>{resteAPayer > 0 ? "Reste à payer" : "Vente réglée"}</span>
            <span data-testid="payment-reste">{resteAPayer.toFixed(2)} €</span>
          </div>

          {resteAPayer > 0 && (
            <>
              <Input
                data-testid="payment-montant-input"
                type="number"
                step="0.01"
                min="0"
                placeholder="Montant remis €"
                value={montantInput}
                onChange={(e) => setMontantInput(e.target.value)}
                className="mb-3"
              />
              <div className="grid grid-cols-2 gap-2">
                {paymentMethods.map((m) => {
                  const Icon = paymentMethodIcon(m.key);
                  const disabled = m.key === "espece" && !currentSession;
                  return (
                    <Button
                      key={m.key}
                      type="button"
                      variant="outline"
                      className="gap-1.5 justify-start"
                      disabled={disabled}
                      title={disabled ? "Ouvrez la caisse (fond de caisse) pour encaisser en espèces" : undefined}
                      onClick={() => applyPayment(m)}
                      data-testid={`payment-method-${m.key}`}
                    >
                      <Icon size={15} /> {m.label}
                    </Button>
                  );
                })}
              </div>
            </>
          )}

          {cashCeilingBlocked && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 mb-4 space-y-2" data-testid="cash-ceiling-non-resident-box">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Paiement en espèces refusé : le plafond légal de 1 000 € par vente est dépassé (art. L112-6 du
                code monétaire et financier). Ce plafond est porté à 15 000 € uniquement pour un client prouvant
                qu'il n'est pas domicilié fiscalement en France — renseignez alors la référence de sa pièce
                d'identité ci-dessous.
              </p>
              <Select value={nonResidentIdType} onValueChange={setNonResidentIdType}>
                <SelectTrigger data-testid="non-resident-id-type"><SelectValue placeholder="Nature de la pièce d'identité présentée" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Passeport">Passeport</SelectItem>
                  <SelectItem value="CNI">Carte nationale d'identité</SelectItem>
                  <SelectItem value="Titre de séjour">Titre de séjour</SelectItem>
                  <SelectItem value="Permis de conduire">Permis de conduire</SelectItem>
                </SelectContent>
              </Select>
              <Input
                data-testid="non-resident-id-ref-input"
                placeholder="Référence pièce d'identité (passeport…)"
                value={nonResidentIdRef}
                onChange={(e) => setNonResidentIdRef(e.target.value)}
              />
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                disabled={!nonResidentIdRef.trim() || validating}
                onClick={() => validate(undefined, nonResidentIdRef.trim())}
                data-testid="non-resident-confirm-button"
              >
                Confirmer client non-résident et valider la vente
              </Button>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} disabled={validating} data-testid="payment-cancel-button">
              <ArrowLeft size={14} className="mr-1.5" /> Retour au panier
            </Button>
            <Button
              className="gap-2"
              disabled={resteAPayer > 0 || validating}
              onClick={() => validate()}
              data-testid="payment-confirm-button"
            >
              <Printer size={16} /> Valider &amp; Imprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sous-dialogue chèque : n° de chèque + nom du titulaire, obligatoires avant
          que le paiement par chèque ne soit ajouté à l'encaissement. */}
      <Dialog open={!!chequePending} onOpenChange={(v) => !v && setChequePending(null)}>
        <DialogContent data-testid="cheque-dialog">
          <DialogHeader><DialogTitle>Paiement par chèque — {chequePending?.montant?.toFixed(2)} €</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input
              data-testid="cheque-numero-input"
              placeholder="N° de chèque"
              value={chequeNumero}
              onChange={(e) => setChequeNumero(e.target.value)}
              autoFocus
            />
            <Input
              data-testid="cheque-nom-input"
              placeholder="Nom du titulaire"
              value={chequeNom}
              onChange={(e) => setChequeNom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmChequePayment()}
            />
          </div>
          <DialogFooter>
            <Button data-testid="cheque-confirm-button" onClick={confirmChequePayment}>Valider le chèque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!carteCadeauPending} onOpenChange={(v) => !v && setCarteCadeauPending(null)}>
        <DialogContent data-testid="carte-cadeau-payment-dialog">
          <DialogHeader><DialogTitle>Paiement par carte cadeau — {carteCadeauPending?.montant?.toFixed(2)} €</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input
              data-testid="carte-cadeau-payment-code-input"
              placeholder="Code à 12 chiffres"
              value={carteCadeauCodeInput}
              onChange={(e) => setCarteCadeauCodeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmCarteCadeauPayment()}
              autoFocus
              maxLength={14}
            />
            {carteCadeauVerifying && <p className="text-xs text-muted-foreground">Vérification...</p>}
            {!carteCadeauVerifying && carteCadeauVerifyResult?.valid && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400" data-testid="carte-cadeau-payment-solde">
                Carte valide — solde disponible : {carteCadeauVerifyResult.solde.toFixed(2)} €
              </p>
            )}
            {!carteCadeauVerifying && carteCadeauVerifyResult && !carteCadeauVerifyResult.valid && (
              <p className="text-xs text-destructive" data-testid="carte-cadeau-payment-error">{carteCadeauVerifyResult.message}</p>
            )}
          </div>
          <DialogFooter>
            <Button data-testid="carte-cadeau-payment-confirm-button" onClick={confirmCarteCadeauPayment} disabled={!carteCadeauVerifyResult?.valid}>
              Valider la carte cadeau
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu "Choisissez la caisse à ouvrir" (mode multi-caisses uniquement) —
          une ligne par caisse (1..nombre_caisses) : libre -> "Ouvrir", déjà
          ouverte par quelqu'un -> "Caisse déjà ouverte, la rejoindre ?". */}
      <Dialog open={caisseChoiceDialog} onOpenChange={setCaisseChoiceDialog}>
        <DialogContent data-testid="caisse-choice-dialog">
          <DialogHeader><DialogTitle>Choisissez la caisse à ouvrir</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {Array.from({ length: shopSettings?.nombre_caisses || 1 }, (_, i) => i + 1).map((numero) => {
              const openForThis = openSessions.find((s) => s.caisse_numero === numero);
              return (
                <div key={numero} className="flex items-center justify-between rounded-lg border p-3" data-testid={`caisse-choice-row-${numero}`}>
                  <div className="text-sm">
                    <span className="font-medium">Caisse {numero}</span>
                    {openForThis ? (
                      <span className="text-muted-foreground block text-xs">
                        Déjà ouverte par {openForThis.ouverte_par_nom} — voulez-vous la rejoindre ?
                        {openForThis.pin_set && " (protégée par code PIN)"}
                      </span>
                    ) : (
                      <span className="text-muted-foreground block text-xs">Libre</span>
                    )}
                  </div>
                  {openForThis ? (
                    <Button
                      size="sm" variant="outline" className="gap-1.5"
                      onClick={() => (openForThis.pin_set ? openJoinPinDialog(openForThis) : joinCaisse(openForThis))}
                      data-testid={`caisse-choice-join-${numero}`}
                    >
                      {openForThis.pin_set ? <KeyRound size={13} /> : <Users size={13} />} Rejoindre
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => pickCaisseToOpen(numero)} data-testid={`caisse-choice-open-${numero}`}>
                      Ouvrir
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Clavier numérique demandé pour rejoindre une caisse protégée par un
          code PIN (voir caisse-choice-join-* ci-dessus, quand pin_set). */}
      <Dialog open={!!joinPinDialog} onOpenChange={(v) => !v && setJoinPinDialog(null)}>
        <DialogContent data-testid="caisse-join-pin-dialog">
          <DialogHeader>
            <DialogTitle>Code PIN — Caisse {joinPinDialog?.caisse_numero}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground text-center">
            Saisissez le code à 3 chiffres pour rejoindre cette caisse.
          </p>
          <div className="space-y-3">
            <PinDots value={joinPin} testidPrefix="caisse-join-pin-keypad" />
            <div className="max-w-[220px] mx-auto">
              <NumericKeypad
                onDigit={joinPinDigit}
                onBackspace={joinPinBackspace}
                disabled={joinPinSaving}
                testIdPrefix="caisse-join-pin-keypad"
                keyClassName={PIN_KEYPAD_KEY_CLASS}
              />
            </div>
          </div>
          {joinPinError && <p className="text-sm text-red-500 text-center" data-testid="caisse-join-pin-error">{joinPinError}</p>}
          <p className="text-xs text-muted-foreground text-center">Le clavier physique fonctionne aussi (chiffres, ⌫, ↵).</p>
          <DialogFooter>
            <Button disabled={joinPinSaving || joinPin.length !== 3} onClick={confirmJoinWithPin} data-testid="caisse-join-pin-confirm">
              {joinPinSaving ? "Vérification..." : "Rejoindre"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Entrée / sortie manuelle d'espèces sur la caisse actuellement ouverte
          (appoint de monnaie, dépôt en banque en cours de journée...). */}
      <Dialog open={sessionMouvementDialog} onOpenChange={setSessionMouvementDialog}>
        <DialogContent data-testid="cash-session-mouvement-dialog">
          <DialogHeader>
            <DialogTitle>{mouvementType === "entree" ? "Entrée d'espèces" : "Sortie d'espèces"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              data-testid="cash-session-mouvement-montant"
              type="number" step="0.01" placeholder="Montant (€)" autoFocus
              value={mouvementMontant} onChange={(e) => setMouvementMontant(e.target.value)}
            />
            <Input
              data-testid="cash-session-mouvement-motif"
              placeholder="Motif (ex. appoint de monnaie, dépôt en banque)"
              value={mouvementMotif} onChange={(e) => setMouvementMotif(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button data-testid="cash-session-mouvement-confirm" disabled={mouvementSaving} onClick={confirmMouvement}>
              {mouvementSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journal détaillé d'une session (bouton "Journal" de l'historique) —
          qui a ouvert/fermé/rejoint/quitté, entrées/sorties d'espèces. */}
      <Dialog open={!!journalDialogSession} onOpenChange={(v) => !v && setJournalDialogSession(null)}>
        <DialogContent data-testid="cash-session-journal-dialog">
          <DialogHeader>
            <DialogTitle>
              Journal — {shopSettings?.caisse_mode === "multiple" && journalDialogSession?.caisse_numero ? `Caisse ${journalDialogSession.caisse_numero}` : "Fond de caisse"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {(journalDialogSession?.journal || []).length === 0 && (
              <p className="text-sm text-muted-foreground">Aucun événement enregistré.</p>
            )}
            {(journalDialogSession?.journal || []).map((entry, idx) => {
              const LABELS = {
                ouverture: "a ouvert la caisse",
                fermeture: "a fermé la caisse",
                rejoint: "a rejoint la caisse",
                quitte: "a quitté la caisse",
                entree_espece: "a fait entrer des espèces",
                sortie_espece: "a fait sortir des espèces",
                pin_reinitialise: "a réinitialisé le code PIN de la caisse",
              };
              const detail = entry.detail || {};
              return (
                <div key={idx} className="flex items-center justify-between text-sm py-1.5 px-2 rounded-md border border-border" data-testid={`cash-session-journal-entry-${idx}`}>
                  <div>
                    <span className="font-medium">{entry.user_nom || "?"}</span>{" "}
                    <span className="text-muted-foreground">{LABELS[entry.type] || entry.type}</span>
                    {(entry.type === "entree_espece" || entry.type === "sortie_espece") && (
                      <span className="text-muted-foreground"> — {Number(detail.montant || 0).toFixed(2)} €{detail.motif ? ` (${detail.motif})` : ""}</span>
                    )}
                    {entry.type === "fermeture" && detail.ecart !== undefined && (
                      <span className="text-muted-foreground"> — écart {detail.ecart > 0 ? "+" : ""}{Number(detail.ecart).toFixed(2)} €</span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">
                    {new Date(entry.at).toLocaleString("fr-FR", { timeZone: "Europe/Paris" })}
                  </span>
                </div>
              );
            })}
          </div>
          {journalDialogSession?.participants?.length > 0 && (
            <div className="pt-2 mt-2 border-t border-border">
              <p className="text-xs font-medium text-muted-foreground mb-1">Participants</p>
              <div className="space-y-1">
                {journalDialogSession.participants.map((p, idx) => (
                  <p key={idx} className="text-xs text-muted-foreground" data-testid={`cash-session-participant-${idx}`}>
                    {p.user_nom} — de {new Date(p.rejoint_le).toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris" })}
                    {p.parti_le ? ` à ${new Date(p.parti_le).toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris" })}` : " (présent)"}
                  </p>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sessionOpenDialog} onOpenChange={setSessionOpenDialog}>
        <DialogContent data-testid="cash-session-open-dialog">
          <DialogHeader>
            <DialogTitle>
              {showOpenPinKeypad
                ? "Code PIN de la caisse"
                : shopSettings?.caisse_mode === "multiple" ? `Ouvrir la caisse ${selectedCaisseNumero}` : "Ouvrir la caisse"}
            </DialogTitle>
          </DialogHeader>
          {showOpenPinKeypad ? (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Définissez le code à 3 chiffres que vos collègues devront saisir pour rejoindre cette caisse.
              </p>
              <div className="space-y-3">
                <PinDots value={openPin} testidPrefix="cash-session-open-pin-keypad" />
                <div className="max-w-[220px] mx-auto">
                  <NumericKeypad
                    onDigit={openPinDigit}
                    onBackspace={openPinBackspace}
                    testIdPrefix="cash-session-open-pin-keypad"
                    keyClassName={PIN_KEYPAD_KEY_CLASS}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Le clavier physique fonctionne aussi (chiffres, ⌫, ↵).</p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowOpenPinKeypad(false)} data-testid="cash-session-open-pin-back">
                  Retour
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Comptez les espèces présentes dans le tiroir avant la première vente : ce montant servira de
                  référence pour détecter un éventuel écart à la fermeture.
                </p>
                <Input
                  data-testid="cash-session-fond-ouverture-input"
                  type="number" step="0.01" placeholder="Fond de caisse de départ (€)"
                  value={fondOuverture} onChange={(e) => setFondOuverture(e.target.value)} autoFocus
                />
                {shopSettings?.caisse_mode === "multiple" && (
                  <Button
                    type="button" variant="outline" size="sm" className="gap-1.5"
                    onClick={() => setShowOpenPinKeypad(true)} data-testid="cash-session-open-pin-button"
                  >
                    <KeyRound size={14} /> {openPin.length === 3 ? "Code PIN défini (•••) — modifier" : "PIN (optionnel)"}
                  </Button>
                )}
              </div>
              <DialogFooter>
                <Button data-testid="cash-session-open-confirm" disabled={sessionSaving} onClick={openSession}>
                  {sessionSaving ? "Ouverture..." : "Ouvrir la caisse"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sessionCloseDialog} onOpenChange={(v) => { setSessionCloseDialog(v); if (!v) setClosePreview(null); }}>
        <DialogContent data-testid="cash-session-close-dialog">
          <DialogHeader><DialogTitle>Fermer la caisse</DialogTitle></DialogHeader>
          {!closePreview ? (
            <>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Comptez les espèces réellement présentes dans le tiroir. Le montant théorique (fond de départ +
                  ventes espèces − remboursements espèces enregistrés) sera comparé automatiquement à votre comptage.
                </p>
                <Input
                  data-testid="cash-session-fond-fermeture-input"
                  type="number" step="0.01" placeholder="Espèces comptées dans le tiroir (€)"
                  value={fondFermetureReel} onChange={(e) => setFondFermetureReel(e.target.value)} autoFocus
                  onKeyDown={(e) => e.key === "Enter" && closeSession()}
                />
              </div>
              <DialogFooter>
                <Button data-testid="cash-session-close-confirm" disabled={sessionSaving} onClick={closeSession}>
                  {sessionSaving ? "Calcul..." : "Continuer"}
                </Button>
              </DialogFooter>
            </>
          ) : Math.abs(closePreview.ecart) < 0.01 ? (
            <>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-center" data-testid="cash-session-ecart-ok">
                <ShieldCheck size={22} className="text-emerald-600 mx-auto mb-1" />
                <p className="font-semibold text-emerald-600">Fond de caisse correct</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Théorique {closePreview.fond_fermeture_theorique.toFixed(2)} € = compté {closePreview.fond_fermeture_reel.toFixed(2)} €
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" disabled={sessionSaving} onClick={() => setClosePreview(null)} data-testid="cash-session-close-back">
                  Retour
                </Button>
                <Button data-testid="cash-session-close-final-confirm" disabled={sessionSaving} onClick={confirmCloseSession}>
                  {sessionSaving ? "Fermeture..." : "Fermer la caisse"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-center" data-testid="cash-session-ecart-warning">
                <ShieldAlert size={22} className="text-amber-600 mx-auto mb-1" />
                <p className="font-semibold text-amber-600">
                  Écart de caisse : {closePreview.ecart > 0 ? "+" : ""}{closePreview.ecart.toFixed(2)} €
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Théorique {closePreview.fond_fermeture_theorique.toFixed(2)} € — compté {closePreview.fond_fermeture_reel.toFixed(2)} €
                </p>
              </div>
              <Textarea
                className="mt-3"
                data-testid="cash-session-notes-input"
                placeholder="D'où pensez-vous que vient cet écart ? (obligatoire)"
                value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)}
                autoFocus
              />
              <DialogFooter>
                <Button variant="outline" disabled={sessionSaving} onClick={() => setClosePreview(null)} data-testid="cash-session-close-back">
                  Retour
                </Button>
                <Button data-testid="cash-session-close-final-confirm" disabled={sessionSaving || !sessionNotes.trim()} onClick={confirmCloseSession}>
                  {sessionSaving ? "Fermeture..." : "Fermer la caisse"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
